/**
 * NW Bundle builder: item CTAs, sticky bar slots, summary, tiered discounts & promo gifts.
 * Promo config: window.nwGlobalPromoConfig (nw-global-promo-config.liquid).
 * Sticky UI config: window.nwBundleStickyBarConfig (nw-bundle-sticky-bar.liquid).
 */
(() => {
  'use strict';

  /**
   * Cart-ready list of sticky-bar lines, refreshed after every add / remove / qty / promo sync.
   * Each: { lineKey, kind, variantId, productId, quantity, sellingPlanId, parentId }
   * sellingPlanId is null when one-time or N/A (Shopify selling plan id as string when set).
   * parentId is the main-set variant id on add-on / gift lines; null on the main set (or when unset).
   */
  window.nwBundleSelectedLines = window.nwBundleSelectedLines || [];

  const SEL = {
    bundleRoot: '[data-nw-bundle-main]',
    stickyBar: '[data-sticky-bar]',
    ctaButton: '.nw-bundle-item__product-item-cta-button',
    qtyWrap: '.nw-bundle-item__product-item-cta-quantity',
    qtyInput: '.nw-bundle-item__product-item-cta-quantity-input',
    stickySlot: '.nw-bundle-sticky-bar__item',
    stickyProduct: '.nw-bundle-sticky-bar__item-product',
    stickyTitle: '.nw-bundle-sticky-bar__item-product-title',
    stickyPrice: '.nw-bundle-sticky-bar__item-product-price',
    stickyImg: '.nw-bundle-sticky-bar__item-product-image img',
    removeSticky: '[data-remove-item]',
    decQty: '[data-decrease-quantity]',
    incQty: '[data-increase-quantity]',
    bundleColumn: '.nw-bundle-item',
    accordionTrigger: '.nw-bundle-item__accordion-trigger',
  };

  const LINE_KIND = {
    PAID: 'paid',
    FREE_POUCH: 'freePouch',
    PROMO_GIFT: 'promoGift',
  };

  /** Line item properties on bundle checkout (`/cart/add.js`). */
  const NW_MATCHA_SET_BUNDLE_PROP = '_matcha_set_bundle';
  const NW_SWATCH_BUNDLE_PROP = '_swatch_bundle';
  const NW_BUNDLE_ROLE_PROP = '_nw_bundle_role';
  const NW_BUNDLE_LINE_KEY_PROP = '_nw_bundle_line_key';
  const NW_BUNDLE_ROLE_MAIN = 'main_set';
  const NW_BUNDLE_ROLE_FREE_POUCH = 'free_pouch';
  const NW_BUNDLE_ROLE_EXTRA = 'extra';
  /**
   * Free-pouch cart lines only — marker for the 100% free-pouch discount function.
   * Do not put this on main-set lines.
   */
  const NW_FREE_POUCH_PROP = '_free_pouch';
  /** On main-set cart lines: comma-separated free pouch names (fulfillment / QA). */
  const NW_FREE_POUCH_TITLE_PROP = '_free_pouch_title';
  const NW_FREE_POUCH_ID_PROP = '_free_pouch_id';
  /** Comma-separated qty per pouch unit (parallel to `_free_pouch_title` / `_free_pouch_id`), e.g. `1, 1`. */
  const NW_FREE_POUCH_QTY_PROP = '_free_pouch_qty';

  const ATTR_PDP_SWATCH_BUNDLE = 'data-nw-bundle-pdp-swatches';

  /** @param {HTMLElement | null | undefined} bundleRoot */
  function shouldAttachMatchaSetBundleLineProp(bundleRoot) {
    if (!bundleRoot) return false;
    if (bundleRoot.hasAttribute(ATTR_PDP_SWATCH_BUNDLE)) return false;
    if (bundleRoot.querySelector('.nw-bundle-swatches')) return false;
    return true;
  }

  /** Swatch PDP (`data-nw-bundle-pdp-swatches`) or `.nw-bundle-swatches` inside bundle root. */
  function shouldAttachSwatchBundleLineProp(bundleRoot) {
    if (!bundleRoot) return false;
    if (bundleRoot.hasAttribute(ATTR_PDP_SWATCH_BUNDLE)) return true;
    if (bundleRoot.querySelector('.nw-bundle-swatches')) return true;
    return false;
  }

  /** Shown subscription total = one-time bundle subtotal × (1 - this). */
  const NW_PURCHASE_SUBSCRIPTION_DISCOUNT = 0.1;

  /** Pixels to leave below the viewport top when auto-scrolling the next bundle column (sticky header clearance). */
  /** Extra top padding when snapping to next `.nw-bundle-item` so content clears sticky header / sticky bar. */
  const NW_BUNDLE_STEP_SCROLL_OFFSET_PX = 250;

  function getCartAddJsUrl() {
    if (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_add_url) {
      return `${KROWN.settings.routes.cart_add_url}.js`;
    }
    return '/cart/add.js';
  }

  /** `nw-bundle-purchase` radios: one-time vs subscription (defaults to onetime if block missing). */
  function nwBundlePurchaseModeFromBundleRoot(bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    const purchase = root?.querySelector('.nw-bundle-purchase');
    const radio = purchase?.querySelector('input[name="nw-bundle-purchase"]:checked');
    return radio && radio.value === 'subscription' ? 'subscription' : 'onetime';
  }

  /** Active main set = paid CTA with `data-is-main-set` and `data-nw-paid-active`; drives `parent_id` on cart lines. */
  function getMainSetParentContext(bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!root) return { productId: null, variantId: null };
    const btn = root.querySelector(
      `${SEL.ctaButton}[data-is-main-set]:not([data-is-free-gift])[${ATTR_PAID_ACTIVE}]`
    );
    if (!btn) return { productId: null, variantId: null };
    const pid = btn.getAttribute('data-product-id');
    const vid = btn.getAttribute('data-variant-id');
    return {
      productId: pid != null && String(pid).trim() !== '' ? String(pid).trim() : null,
      variantId: vid != null && String(vid).trim() !== '' ? String(vid).trim() : null,
    };
  }

  /**
   * Backfill sellingPlanId from each line's CTA (when that block has enable_subscription) or main-set swatch.
   * Lines without a plan stay one-time in subscription checkout.
   */
  function enrichBundleLinesForCart(lines, mode, bundleRoot) {
    if (!Array.isArray(lines) || !bundleRoot || mode !== 'subscription') return lines;

    return lines.map((line) => {
      if (line == null || line.kind === LINE_KIND.PROMO_GIFT) return line;
      if (line.sellingPlanId != null && String(line.sellingPlanId).trim() !== '') return line;

      const btn = paidCtaForVariant(bundleRoot, line.variantId);
      const sp = btn?.getAttribute('data-selling-plan-id');
      if (sp != null && String(sp).trim() !== '') {
        return { ...line, sellingPlanId: String(sp).trim() };
      }

      const mainCtx = getMainSetParentContext(bundleRoot);
      if (mainCtx.variantId && String(line.variantId) === String(mainCtx.variantId)) {
        const sw = bundleRoot.querySelector('.nw-bundle-swatches input[name="nw-bundle-swatches"]:checked');
        const swSp = sw?.getAttribute('data-selling-plan-id');
        if (swSp != null && String(swSp).trim() !== '') {
          return { ...line, sellingPlanId: String(swSp).trim() };
        }
      }

      return line;
    });
  }

  /** @param {object} bundleRoot */
  function resolveBundleLineRole(line, bundleRoot) {
    if (line.kind === LINE_KIND.FREE_POUCH) return NW_BUNDLE_ROLE_FREE_POUCH;
    if (line.kind === LINE_KIND.PAID && bundleRoot && line.variantId) {
      const btn = paidCtaForVariant(bundleRoot, line.variantId);
      if (btn && isMainSetPaidButton(btn)) return NW_BUNDLE_ROLE_MAIN;
    }
    return NW_BUNDLE_ROLE_EXTRA;
  }

  /** @param {object} bundleRoot @param {string} role @param {string} [lineKey] @param {{ label?: string, ids?: string, qty?: string } | null} [freePouchMeta] */
  function matchaSetBundleLineProperties(bundleRoot, role, lineKey, freePouchMeta) {
    if (!shouldAttachMatchaSetBundleLineProp(bundleRoot)) return null;
    /** @type {Record<string, string>} */
    const props = {
      [NW_MATCHA_SET_BUNDLE_PROP]: 'true',
      [NW_BUNDLE_ROLE_PROP]: role,
    };
    if (lineKey) props[NW_BUNDLE_LINE_KEY_PROP] = lineKey;
    if (role === NW_BUNDLE_ROLE_FREE_POUCH) {
      props[NW_FREE_POUCH_PROP] = 'true';
    }
    if (freePouchMeta && freePouchMeta.label && role === NW_BUNDLE_ROLE_MAIN) {
      props[NW_FREE_POUCH_TITLE_PROP] = freePouchMeta.label;
      if (freePouchMeta.ids) props[NW_FREE_POUCH_ID_PROP] = freePouchMeta.ids;
      if (freePouchMeta.qty) props[NW_FREE_POUCH_QTY_PROP] = freePouchMeta.qty;
    }
    return props;
  }

  /** Names, variant ids, and per-unit qty (one entry per pouch unit) for main-set line properties. */
  function collectFreePouchMainSetProps(stickyRoot) {
    if (!stickyRoot) return { label: '', ids: '', qty: '' };
    const names = [];
    const ids = [];
    const qtys = [];
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind !== LINE_KIND.FREE_POUCH) continue;
      const titleEl = slot.querySelector(SEL.stickyTitle);
      const name = (titleEl && titleEl.textContent.trim()) || 'Free pouch';
      const vid = slot.dataset.nwVariantId || '';
      const slotQty = parseInt10(slot.dataset.nwQty, 1);
      for (let i = 0; i < slotQty; i += 1) {
        names.push(name);
        if (vid) ids.push(String(vid));
        qtys.push('1');
      }
    }
    return {
      label: names.join(', '),
      ids: ids.join(', '),
      qty: qtys.join(', '),
    };
  }

  /** Comma-separated pouch product titles (one entry per pouch unit) for main-set line properties. */
  function collectFreePouchSummaryLabel(stickyRoot) {
    return collectFreePouchMainSetProps(stickyRoot).label;
  }

  /**
   * Free pouch must match main-set qty 1:1 when the free-pouch step exists
   * (e.g. 2 gift sets → 2 free pouches before ATC / extras unlock).
   */
  function isFreePouchRequirementMet(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return false;
    if (!bundleIncludesFreePouchStep(root)) return true;
    const mainQty = mainSetCapacity(stickyRoot, root);
    if (mainQty < 1) return false;
    return freePouchQuantitySum(stickyRoot) >= mainQty;
  }

  function bundleReadyForAddToCart(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return false;
    const mainQty = mainSetCapacity(stickyRoot, root);
    if (mainQty < 1) return false;
    return isFreePouchRequirementMet(stickyRoot, root);
  }

  function syncAddToCartButtonsState(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    const ready = bundleReadyForAddToCart(stickyRoot, root);
    const setBtn = (btn) => {
      if (!btn) return;
      btn.disabled = !ready;
      if (!ready) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    };
    if (stickyRoot) {
      stickyRoot
        .querySelectorAll(
          '.nw-bundle-sticky-bar__cta-item-button:not(.nw-bundle-sticky-bar__cta-item-button-buy-now), .nw-bundle-sticky-bar__cta-item-button-subscribe'
        )
        .forEach(setBtn);
    }
    if (root) {
      root.querySelectorAll('.nw-bundle-purchase__cta-button, .nw-bundle-purchase__cta-button-subscribe').forEach(setBtn);
    }
  }

  /**
   * One cart line per free-pouch unit (qty 1) with a unique line-key property so Shopify does not merge
   * them and Upcart cannot inflate a single line above the gift-set allowance.
   */
  function expandFreePouchLineToCartItems(line, bundleRoot, mainParentVariantNum) {
    const vid = parseInt10(line.variantId, 0);
    if (!vid) return [];
    const unitQty = Math.max(1, parseInt10(line.quantity, 1));
    const baseKey = line.lineKey || `free-pouch-${vid}`;
    /** @type {Array<{ id: number, quantity: number, parent_id?: number, properties: Record<string, string> }>} */
    const items = [];
    for (let i = 0; i < unitQty; i += 1) {
      const props = matchaSetBundleLineProperties(
        bundleRoot,
        NW_BUNDLE_ROLE_FREE_POUCH,
        `${baseKey}__${i}`
      );
      if (!props) continue;
      /** @type {{ id: number, quantity: number, parent_id?: number, properties: Record<string, string> }} */
      const payload = { id: vid, quantity: 1, properties: props };
      if (mainParentVariantNum) payload.parent_id = mainParentVariantNum;
      items.push(payload);
    }
    return items;
  }

  /** Merge bundle lines for Cart API: `onetime` omits selling_plan; `subscription` adds it when present on the line. */
  function buildCartItemsFromBundleLines(lines, mode, bundleRoot, stickyRoot) {
    const { variantId: mainVariantId } = getMainSetParentContext(bundleRoot);
    const mainParentVariantNum = mainVariantId ? parseInt10(mainVariantId, 0) : 0;
    const root = bundleRoot || getBundleRoot();
    const freePouchMeta = collectFreePouchMainSetProps(stickyRoot);

    /** @type {Map<string, { id: number, quantity: number, selling_plan?: number, isMainSet: boolean, useParentId: boolean, role: string }>} */
    const merge = new Map();
    /** @type {Array<{ id: number, quantity: number, selling_plan?: number, parent_id?: number, properties?: Record<string, string>, isMainSet: boolean }>} */
    const freePouchItems = [];

    for (const line of lines) {
      if (line == null || line.kind === LINE_KIND.PROMO_GIFT) continue;
      if (line.variantId == null || line.variantId === '') continue;
      const vid = parseInt10(line.variantId, 0);
      if (!vid) continue;

      if (line.kind === LINE_KIND.FREE_POUCH) {
        freePouchItems.push(...expandFreePouchLineToCartItems(line, bundleRoot, mainParentVariantNum));
        continue;
      }

      const qty = Math.max(1, parseInt10(line.quantity, 1));
      let sellingPlan;
      if (mode === 'subscription' && line.sellingPlanId != null && String(line.sellingPlanId).trim() !== '') {
        const pid = parseInt10(line.sellingPlanId, 0);
        if (pid) sellingPlan = pid;
      }
      const role = resolveBundleLineRole(line, root);
      const lineIsMainSet = role === NW_BUNDLE_ROLE_MAIN;
      const useParentId = !lineIsMainSet;

      const key = `${role}__${vid}__${sellingPlan || 0}`;
      const cur = merge.get(key);
      if (cur) {
        cur.quantity += qty;
      } else {
        merge.set(key, {
          id: vid,
          quantity: qty,
          selling_plan: sellingPlan,
          isMainSet: lineIsMainSet,
          useParentId,
          role,
        });
      }
    }

    return [...merge.values()]
      .sort((a, b) => {
        if (a.isMainSet && !b.isMainSet) return -1;
        if (!a.isMainSet && b.isMainSet) return 1;
        return 0;
      })
      .map((item) => {
        /** @type {{ id: number, quantity: number, selling_plan?: number, parent_id?: number, properties?: Record<string, string> }} */
        const payload = {
          id: item.id,
          quantity: item.quantity,
        };
        const matchaProps = matchaSetBundleLineProperties(
          bundleRoot,
          item.role,
          undefined,
          item.role === NW_BUNDLE_ROLE_MAIN ? freePouchMeta : null
        );
        if (matchaProps) payload.properties = matchaProps;
        else if (shouldAttachSwatchBundleLineProp(bundleRoot)) {
          payload.properties = { [NW_SWATCH_BUNDLE_PROP]: 'true' };
        }
        if (item.selling_plan) payload.selling_plan = item.selling_plan;
        if (item.useParentId && mainParentVariantNum) payload.parent_id = mainParentVariantNum;
        return payload;
      })
      .concat(freePouchItems);
  }

  function nwBundleRefreshCartFromSection() {
    if (typeof fetch === 'undefined') return Promise.resolve();
    return fetch('?section_id=helper-cart')
      .then((r) => r.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const cartForm = document.getElementById('AjaxCartForm');
        const cartSub = document.getElementById('AjaxCartSubtotal');
        const formSrc = doc.getElementById('AjaxCartForm');
        const subSrc = doc.getElementById('AjaxCartSubtotal');
        if (cartForm && formSrc) {
          cartForm.innerHTML = formSrc.innerHTML;
          if (typeof cartForm.ajaxifyCartItems === 'function') cartForm.ajaxifyCartItems();
        }
        if (cartSub && subSrc) cartSub.innerHTML = subSrc.innerHTML;
        const countSrc = doc.querySelector('#AjaxCartForm [data-cart-count]');
        if (countSrc) {
          document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
            elm.textContent = countSrc.textContent;
          });
        }
        const totalSrc = doc.querySelector('#AjaxCartForm [data-cart-total]');
        if (totalSrc) {
          document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
            elm.textContent = totalSrc.textContent;
          });
        }
      });
  }

  /** Refresh theme cart fragments and notify Upcart / cart drawer listeners. */
  function nwBundleNotifyCartAfterAdd() {
    return nwBundleRefreshCartFromSection().then(() => {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new Event('add-to-cart', { bubbles: true }));
      if (typeof window.nwScheduleBundleCartGuard === 'function') {
        window.nwScheduleBundleCartGuard();
      }
    });
  }

  /**
   * True when the bundle markup includes step 2 (see `nw-bundle-item.liquid`: `data-nw-bundle-accordion-role="free-pouch"`).
   * Uses only accordion role — not `[data-is-free-gift]` — so mis-tagged CTAs cannot trigger this rule wrongly.
   */
  function bundleIncludesFreePouchStep(bundleRoot) {
    if (getFreePouchTotalQtyCap() <= 0) return false;
    const root = bundleRoot || getBundleRoot();
    if (!root) return false;
    const col = root.querySelector(`${SEL.bundleColumn}[data-nw-bundle-accordion-role="free-pouch"]`);
    return Boolean(col && !col.hidden && !col.hasAttribute('hidden'));
  }

  /**
   * @param {'onetime' | 'subscription'} mode
   */
  function nwBundleAddToCart(mode, bundleRoot, triggerEl) {
    const sticky = getStickyRoot();
    if (sticky) {
      syncNwBundleSelectedLines(sticky);
    }
    const rawLines = window.nwBundleSelectedLines || [];
    const lines = enrichBundleLinesForCart(rawLines, mode, bundleRoot);
    const items = buildCartItemsFromBundleLines(lines, mode, bundleRoot, sticky);
    if (items.length === 0) return;

    if (triggerEl) triggerEl.classList.add('working');

    const fail = (msg) => {
      window.alert(msg);
      if (triggerEl) triggerEl.classList.remove('working');
    };

    if (sticky && bundleRoot && bundleIncludesFreePouchStep(bundleRoot)) {
      const mainQty = mainSetCapacity(sticky, bundleRoot);
      const pouchQty = freePouchQuantitySum(sticky);
      if (mainQty < 1) {
        fail('Please choose your gift set before adding to cart.');
        return;
      }
      if (!isFreePouchRequirementMet(sticky, bundleRoot)) {
        fail(
          mainQty === 1
            ? 'Please select your free pouch before adding to cart.'
            : `Please select ${mainQty} free pouches (one per gift set). You have selected ${pouchQty}.`
        );
        return;
      }
    }

    if (mode === 'subscription') {
      const hasAnySubLine = lines.some(
        (l) =>
          l &&
          l.kind !== LINE_KIND.PROMO_GIFT &&
          l.variantId != null &&
          String(l.variantId).trim() !== '' &&
          l.sellingPlanId != null &&
          String(l.sellingPlanId).trim() !== ''
      );
      if (!hasAnySubLine) {
        fail('No selected products support subscription.');
        return;
      }
    }

    fetch(getCartAddJsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (response.status === 422 || data.status === 422) {
          const msg =
            typeof data.description === 'string'
              ? data.description
              : typeof data.message === 'string'
                ? data.message
                : 'Could not add to cart';
          fail(msg);
          return;
        }
        if (!response.ok) {
          fail('Could not add to cart');
          return;
        }
        return nwBundleNotifyCartAfterAdd();
      })
      .catch((err) => {
        if (err instanceof Error && err.message && !err.message.includes('fetch')) {
          fail(err.message);
        } else {
          fail('Could not add to cart');
        }
      })
      .finally(() => {
        if (triggerEl) triggerEl.classList.remove('working');
      });
  }

  /** Added while free pouch shows selected label (for CSS hooks). */
  const CLS_FREE_POUCH_SELECTED = 'nw-bundle-item__product-item-cta-button--is-selected';
  /** Sticky cell for the main-set SKU when that column is configured as a free gift (see nw-bundle-item). */
  const CLS_STICKY_MAIN_SET_FREE_GIFT = 'nw-bundle-sticky-bar__item--main-set-free-gift';

  const ATTR_PAID_ACTIVE = 'data-nw-paid-active';

  /** @returns {NwStickyConfig} */
  function getStickyConfig() {
    const global = window.nwGlobalPromoConfig || {};
    const local = window.nwBundleStickyBarConfig || {};
    return {
      ...global,
      ...local,
      shippingThreshold: global.shippingThreshold ?? 3499,
      tieredDiscounts: global.tieredDiscounts || [],
      freeGifts: global.freeGifts || [],
      freeGiftThresholdMajorUnits: false,
    };
  }

  /** Theme: max paid main-set units combined (default 1). */
  function getMainSetTotalQtyCap() {
    const raw = getStickyConfig().mainSetTotalQtyCap;
    const n = typeof raw === 'number' ? raw : parseInt10(raw, 1);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.floor(n);
  }

  /** Theme: max free pouch lines (default 1). Effective cap is also bounded by current main-set qty in the bar. */
  function getFreePouchTotalQtyCap() {
    const raw = getStickyConfig().freePouchTotalQtyCap;
    const n = typeof raw === 'number' ? raw : parseInt10(raw, 1);
    if (!Number.isFinite(n) || n < 0) return 1;
    return Math.floor(n);
  }

  /** Theme setting: max paid extra (add-on) units combined (default 4). */
  function getExtrasTotalQtyCap() {
    const raw = getStickyConfig().extraProductsTotalQtyCap;
    const n = typeof raw === 'number' ? raw : parseInt10(raw, 4);
    if (!Number.isFinite(n) || n < 0) return 4;
    return Math.floor(n);
  }

  function formatMoney(cents) {
    const n = Number(cents);
    if (Number.isNaN(n)) return '—';
    if (typeof window.KROWN !== 'undefined' && KROWN.settings && KROWN.settings.shop_money_format) {
      return formatShopifyCents(n, KROWN.settings.shop_money_format);
    }
    return (n / 100).toFixed(2);
  }

  function formatShopifyCents(cents, formatString) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return (cents / 100).toFixed(2);

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      const iso = (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.iso_code) ? KROWN.settings.iso_code : '';
      return dollarsAmount + centsAmount + iso;
    }

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = (cents / 100).toFixed(2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function parseInt10(v, fallback = 0) {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? fallback : n;
  }

  function getStickyRoot() {
    return document.querySelector(SEL.stickyBar);
  }

  function getBundleRoot() {
    return document.querySelector(SEL.bundleRoot);
  }

  function isEmptyStaticPlaceholder(el) {
    return (
      el != null &&
      el.matches(SEL.stickySlot) &&
      el.classList.contains('nw-bundle-sticky-bar__item--empty') &&
      !el.hasAttribute('data-nw-dynamic-promo') &&
      !el.hasAttribute('data-nw-dynamic-user')
    );
  }

  /** Sticky indices reserved for bundle messaging (free shipping / gift); never receive main, pouch, or paid extras. */
  function isBundleMilestoneOnlySlot(slot) {
    if (!slot) return false;
    return (
      slot.hasAttribute('data-nw-bundle-shipping-milestone') ||
      slot.hasAttribute('data-nw-bundle-gift-milestone') ||
      slot.hasAttribute('data-nw-bundle-gift-milestone-2')
    );
  }

  /** Slot 2 is the free-pouch placeholder; hidden when theme cap is 0 (never fill it). */
  function isFreePouchSlotHidden(slot) {
    return Boolean(
      slot &&
        (slot.hidden ||
          slot.hasAttribute('hidden') ||
          slot.classList.contains('nw-bundle-sticky-bar__item--free-pouch-slot-hidden'))
    );
  }

  /**
   * When Free pouch — max total quantity is 0: hide the free-pouch accordion and sticky slot 2
   * (unless slot 2 is a shipping/gift milestone).
   */
  function syncFreePouchCapVisibility(bundleRoot, stickyRoot) {
    const disabled = getFreePouchTotalQtyCap() <= 0;
    const root = bundleRoot || getBundleRoot();
    if (root) {
      for (const col of root.querySelectorAll(`${SEL.bundleColumn}[data-nw-bundle-accordion-role="free-pouch"]`)) {
        col.hidden = disabled;
        col.classList.toggle('nw-bundle-item--free-pouch-hidden', disabled);
      }
    }
    const sticky = stickyRoot || getStickyRoot();
    if (!sticky) return;
    const slot2 = sticky.querySelector(`${SEL.stickySlot}[data-nw-sticky-slot="2"]`);
    if (!slot2 || isBundleMilestoneOnlySlot(slot2)) return;
    slot2.hidden = disabled;
    slot2.classList.toggle('nw-bundle-sticky-bar__item--free-pouch-slot-hidden', disabled);
  }

  /**
   * Reserved static column for threshold gift 1 or 2 (when configured in theme).
   * @param {1|2|string|number} cfgSlot — freeGifts[].slot from config
   */
  function findStaticPromoGiftMilestoneSlot(stickyRoot, cfgSlot) {
    if (!stickyRoot) return null;
    const n = Number(cfgSlot);
    const sel =
      n === 2 ? '[data-nw-bundle-gift-milestone-2]' : '[data-nw-bundle-gift-milestone]';
    const el = stickyRoot.querySelector(sel);
    if (!el || !el.matches(SEL.stickySlot)) return null;
    if (!el.classList.contains('nw-bundle-sticky-bar__item--empty')) return null;
    if (el.hasAttribute('data-nw-dynamic-promo') || el.hasAttribute('data-nw-dynamic-user')) return null;
    return el;
  }

  /** Paid / pouch columns in DOM order (excludes shipping + gift milestones and promo template rows). */
  function getProductEligibleSlotsInOrder(stickyRoot) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return [];
    const out = [];
    for (const s of items.querySelectorAll(SEL.stickySlot)) {
      if (s.hasAttribute('data-nw-dynamic-promo')) continue;
      if (isBundleMilestoneOnlySlot(s)) continue;
      if (isFreePouchSlotHidden(s)) continue;
      out.push(s);
    }
    return out;
  }

  /** An empty product slot appears before a filled paid/pouch line (middle remove leaves a hole). */
  function movableSlotsHaveLeadingGap(stickyRoot) {
    const eligible = getProductEligibleSlotsInOrder(stickyRoot);
    let seenEmpty = false;
    for (const s of eligible) {
      const empty = s.classList.contains('nw-bundle-sticky-bar__item--empty');
      const movableFilled =
        !empty &&
        (s.dataset.nwLineKind === LINE_KIND.PAID || s.dataset.nwLineKind === LINE_KIND.FREE_POUCH);
      if (empty) seenEmpty = true;
      else if (seenEmpty && movableFilled) return true;
    }
    return false;
  }

  function stickySlotToPayload(slot) {
    const kind = slot.dataset.nwLineKind;
    const prod = slot.querySelector(SEL.stickyProduct);
    const imgEl = prod?.querySelector(SEL.stickyImg);
    const image = (imgEl && imgEl.getAttribute('src')) || '';
    const title = prod?.querySelector(SEL.stickyTitle)?.textContent?.trim() || '';
    const unitCents = kind === LINE_KIND.FREE_POUCH ? 0 : parseInt10(slot.dataset.nwUnitCents, 0);
    const qty = parseInt10(slot.dataset.nwQty, 1);
    const sp = slot.dataset.nwSellingPlanId;
    const pid = slot.dataset.nwProductId;
    const payload = {
      lineKey: slot.dataset.nwLineKey,
      kind,
      variantId: slot.dataset.nwVariantId,
      productId: pid != null && pid !== '' ? String(pid) : null,
      sellingPlanId: sp != null && String(sp).trim() !== '' ? String(sp) : null,
      title,
      image,
      unitCents,
      qty,
    };
    if (kind === LINE_KIND.FREE_POUCH) {
      payload.priceLabel = 'Free';
    }
    return payload;
  }

  function removeTrailingEmptyDynamicUserSlots(stickyRoot) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return;
    const slots = [...items.querySelectorAll(SEL.stickySlot)];
    let lastNonEmpty = -1;
    slots.forEach((s, i) => {
      if (!s.classList.contains('nw-bundle-sticky-bar__item--empty')) lastNonEmpty = i;
    });
    for (let i = slots.length - 1; i > lastNonEmpty; i--) {
      const s = slots[i];
      if (
        s.hasAttribute('data-nw-dynamic-user') &&
        s.classList.contains('nw-bundle-sticky-bar__item--empty')
      ) {
        s.remove();
      }
    }
  }

  /** Shift paid + free-pouch lines left into the first eligible cells (milestones stay fixed). */
  function recompactMovableStickySlots(stickyRoot) {
    if (!stickyRoot) return;
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return;

    const movable = [...items.querySelectorAll(SEL.stickySlot)].filter(
      (s) =>
        !s.classList.contains('nw-bundle-sticky-bar__item--empty') &&
        (s.dataset.nwLineKind === LINE_KIND.PAID || s.dataset.nwLineKind === LINE_KIND.FREE_POUCH)
    );

    if (movable.length === 0) {
      removeTrailingEmptyDynamicUserSlots(stickyRoot);
      return;
    }

    const payloads = movable.map(stickySlotToPayload);
    for (const s of movable) {
      clearStickySlot(s);
    }

    let targets = getProductEligibleSlotsInOrder(stickyRoot);
    let anchor = targets.length ? targets[targets.length - 1] : null;
    while (targets.length < payloads.length) {
      const n = insertDynamicUserSlotAfter(stickyRoot, anchor);
      if (!n) break;
      targets.push(n);
      anchor = n;
    }

    const nFill = Math.min(payloads.length, targets.length);
    for (let i = 0; i < nFill; i++) {
      fillStickySlot(targets[i], payloads[i], { scrollIntoView: false });
    }
    if (nFill > 0) {
      ensureStickySlotVisibleInItemsRow(targets[nFill - 1]);
    }

    removeTrailingEmptyDynamicUserSlots(stickyRoot);
  }

  /**
   * Next placeholder for user-selected lines only (excludes dynamic promo cells — they never consume tier slots).
   */
  function findFirstEmptySlot(stickyRoot) {
    if (!stickyRoot) return null;
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    const scope = items || stickyRoot;
    for (const slot of scope.querySelectorAll(`${SEL.stickySlot}.nw-bundle-sticky-bar__item--empty`)) {
      if (slot.hasAttribute('data-nw-dynamic-promo')) continue;
      if (slot.hasAttribute('data-nw-dynamic-user')) continue;
      if (isBundleMilestoneOnlySlot(slot)) continue;
      if (isFreePouchSlotHidden(slot)) continue;
      return slot;
    }
    return null;
  }

  /** First empty tier placeholder strictly to the right of `afterEl` (same strip). */
  function findNextEmptyStaticAfter(afterEl) {
    let n = afterEl ? afterEl.nextElementSibling : null;
    while (n && n.matches(SEL.stickySlot)) {
      if (isEmptyStaticPlaceholder(n) && !isBundleMilestoneOnlySlot(n) && !isFreePouchSlotHidden(n)) {
        return n;
      }
      n = n.nextElementSibling;
    }
    return null;
  }

  /** Last non-empty slot in the strip (DOM order), so promos sit after the final user-selected cell. */
  function findLastFilledItemSlot(stickyRoot) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return null;
    const slots = [...items.querySelectorAll(SEL.stickySlot)].filter(
      (s) => !s.classList.contains('nw-bundle-sticky-bar__item--empty')
    );
    return slots.length ? slots[slots.length - 1] : null;
  }

  /**
   * `.nw-bundle-sticky-bar__items` uses overflow-x: auto; nudge scroll when a filled slot is outside the visible strip.
   */
  function ensureStickySlotVisibleInItemsRow(slot) {
    if (!slot || typeof slot.getBoundingClientRect !== 'function') return;
    const items = slot.closest('.nw-bundle-sticky-bar__items');
    if (!items) return;
    const pad = 16;
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    const run = () => {
      const wrap = items.getBoundingClientRect();
      const r = slot.getBoundingClientRect();
      if (r.width <= 0 && r.height <= 0) return;
      let dx = 0;
      if (r.left < wrap.left + pad) {
        dx = r.left - wrap.left - pad;
      } else if (r.right > wrap.right - pad) {
        dx = r.right - wrap.right + pad;
      }
      if (dx !== 0) {
        items.scrollBy({ left: dx, behavior });
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }

  /**
   * Clone tag-free slot markup; insert directly after `insertAfterEl`, or append if no anchor.
   */
  function insertDynamicPromoSlotAfter(stickyRoot, insertAfterEl) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return null;
    const template = document.getElementById('nw-bundle-sticky-bar__item-template');
    if (!template || !template.content || !template.content.firstElementChild) return null;
    const node = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
    if (insertAfterEl && items.contains(insertAfterEl)) {
      insertAfterEl.insertAdjacentElement('afterend', node);
    } else {
      items.appendChild(node);
    }
    return node;
  }

  /**
   * Extra static placeholder cells for paid qty (same structure as promo template, tagged as user-dynamic).
   */
  function insertDynamicUserSlotAfter(stickyRoot, insertAfterEl) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return null;
    const template = document.getElementById('nw-bundle-sticky-bar__item-template');
    if (!template || !template.content || !template.content.firstElementChild) return null;
    const node = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
    node.removeAttribute('data-nw-dynamic-promo');
    node.setAttribute('data-nw-dynamic-user', '');
    if (insertAfterEl && items.contains(insertAfterEl)) {
      insertAfterEl.insertAdjacentElement('afterend', node);
    } else {
      items.appendChild(node);
    }
    return node;
  }

  function makePaidLineKey(variantId) {
    const suffix =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    return `paid-${variantId}::${suffix}`;
  }

  function paidSlotsForVariant(stickyRoot, variantId) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return [];
    const vid = String(variantId);
    const out = [];
    for (const s of items.querySelectorAll(`${SEL.stickySlot}:not(.nw-bundle-sticky-bar__item--empty)`)) {
      if (s.dataset.nwLineKind === LINE_KIND.PAID && String(s.dataset.nwVariantId) === vid) {
        out.push(s);
      }
    }
    return out;
  }

  function removePaidSlotElement(slot) {
    if (!slot) return;
    if (slot.hasAttribute('data-nw-dynamic-user')) {
      slot.remove();
    } else {
      clearStickySlot(slot);
    }
  }

  function addPaidUnitSlot(stickyRoot, btn, bundleRoot) {
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId || !stickyRoot) return false;
    const root = bundleRoot || getBundleRoot();
    if (isMainSetPaidButton(btn) && root && mainSetCapacity(stickyRoot, root) >= getMainSetTotalQtyCap()) {
      return false;
    }
    if (root && isExtrasPaidButton(btn) && !extrasFlowUnlocked(stickyRoot, root)) {
      return false;
    }
    if (root && isExtrasPaidButton(btn) && extrasTotalPaidQty(stickyRoot, root) >= getExtrasTotalQtyCap()) {
      return false;
    }
    const lineKey = makePaidLineKey(variantId);
    const unit = unitCentsFromButton(btn);

    const lastGlobal = findLastFilledItemSlot(stickyRoot);
    let slot = lastGlobal ? findNextEmptyStaticAfter(lastGlobal) : null;
    if (!slot) {
      slot = findFirstEmptySlot(stickyRoot);
    }
    if (!slot) {
      slot = insertDynamicUserSlotAfter(stickyRoot, lastGlobal);
    }
    if (!slot) return false;
    fillStickySlot(slot, {
      lineKey,
      kind: LINE_KIND.PAID,
      variantId,
      productId: btn.getAttribute('data-product-id'),
      sellingPlanId: btn.getAttribute('data-selling-plan-id'),
      title: btn.getAttribute('data-product-title') || '',
      image: btn.getAttribute('data-product-image') || '',
      unitCents: unit,
      qty: 1,
    });
    return true;
  }

  /** Grow or shrink paid sticky cells to match `targetQ` (each cell qty 1). */
  function syncPaidSlotsToQty(stickyRoot, bundleRoot, btn, targetQ) {
    const vid = btn.getAttribute('data-variant-id');
    if (!vid || !stickyRoot) return 0;
    const root = bundleRoot || getBundleRoot();
    if (isMainSetPaidButton(btn) && root) {
      const used = mainSetCapacity(stickyRoot, root);
      const thisN = paidSlotsForVariant(stickyRoot, vid).length;
      const capForLine = getMainSetTotalQtyCap() - used + thisN;
      targetQ = Math.min(targetQ, capForLine);
    }
    if (isExtrasPaidButton(btn) && root) {
      const used = extrasTotalPaidQty(stickyRoot, root);
      const thisN = paidSlotsForVariant(stickyRoot, vid).length;
      const capForLine = getExtrasTotalQtyCap() - used + thisN;
      targetQ = Math.min(targetQ, capForLine);
    }
    let n = paidSlotsForVariant(stickyRoot, vid).length;
    if (targetQ > n) {
      while (n < targetQ) {
        if (!addPaidUnitSlot(stickyRoot, btn, root)) break;
        n = paidSlotsForVariant(stickyRoot, vid).length;
      }
    } else if (targetQ < n) {
      while (n > targetQ) {
        const slots = paidSlotsForVariant(stickyRoot, vid);
        const last = slots[slots.length - 1];
        if (!last) break;
        removePaidSlotElement(last);
        n--;
      }
    }
    return paidSlotsForVariant(stickyRoot, vid).length;
  }

  function clearStickySlot(slot) {
    if (!slot) return;
    slot.classList.add('nw-bundle-sticky-bar__item--empty');
    slot.classList.remove(CLS_STICKY_MAIN_SET_FREE_GIFT);
    delete slot.dataset.nwLineKey;
    delete slot.dataset.nwLineKind;
    delete slot.dataset.nwVariantId;
    delete slot.dataset.nwQty;
    delete slot.dataset.nwUnitCents;
    delete slot.dataset.nwPromoSlot;
    delete slot.dataset.nwProductId;
    delete slot.dataset.nwSellingPlanId;
    const prod = slot.querySelector(SEL.stickyProduct);
    if (!prod) return;
    const titleEl = prod.querySelector(SEL.stickyTitle);
    const priceEl = prod.querySelector(SEL.stickyPrice);
    const img = prod.querySelector(SEL.stickyImg);
    if (titleEl) titleEl.textContent = '';
    if (priceEl) priceEl.textContent = '';
    if (img) {
      img.src = '';
      img.alt = '';
    }
    const rm = prod.querySelector(SEL.removeSticky);
    if (rm) rm.hidden = false;
  }

  /**
   * @param {HTMLElement} slot
   * @param {{
   *   lineKey: string,
   *   kind: string,
   *   variantId: string,
   *   title: string,
   *   image: string,
   *   unitCents: number,
   *   qty: number,
   *   priceLabel?: string,
   *   promoSlot?: string,
   *   productId?: string | null,
   *   sellingPlanId?: string | null
   * }} payload
   * @param {{ scrollIntoView?: boolean }} [options] — default true; set false for batched fills (e.g. recompact).
   */
  function fillStickySlot(slot, payload, options) {
    const scrollIntoView = options == null || options.scrollIntoView !== false;
    const prod = slot.querySelector(SEL.stickyProduct);
    if (!prod) return;
    // Free pouch is always £0 in the builder (Shopify Function applies 100% discount at cart).
    if (payload.kind === LINE_KIND.FREE_POUCH) {
      payload.unitCents = 0;
      payload.priceLabel = 'Free';
    }
    slot.classList.remove('nw-bundle-sticky-bar__item--empty');
    slot.dataset.nwLineKey = payload.lineKey;
    slot.dataset.nwLineKind = payload.kind;
    slot.dataset.nwVariantId = String(payload.variantId);
    slot.dataset.nwQty = String(payload.qty);
    slot.dataset.nwUnitCents = String(payload.unitCents);
    if (payload.promoSlot != null) slot.dataset.nwPromoSlot = String(payload.promoSlot);
    if (payload.productId != null && payload.productId !== '') {
      slot.dataset.nwProductId = String(payload.productId);
    } else {
      delete slot.dataset.nwProductId;
    }
    if (payload.sellingPlanId != null && payload.sellingPlanId !== '') {
      slot.dataset.nwSellingPlanId = String(payload.sellingPlanId);
    } else {
      delete slot.dataset.nwSellingPlanId;
    }

    const titleEl = prod.querySelector(SEL.stickyTitle);
    const priceEl = prod.querySelector(SEL.stickyPrice);
    const img = prod.querySelector(SEL.stickyImg);
    if (titleEl) titleEl.textContent = payload.title;
    const priceText =
      payload.priceLabel != null
        ? payload.priceLabel
        : formatMoney(payload.unitCents * payload.qty);
    if (priceEl) priceEl.textContent = priceText;
    if (img) {
      let url = payload.image || '';
      if (payload.kind === LINE_KIND.PROMO_GIFT) {
        const tagActive = img.getAttribute('data-nw-bundle-tag-active-src');
        if (tagActive) url = tagActive;
      }
      img.src = url;
      img.alt = payload.title;
    }
    const rm = prod.querySelector(SEL.removeSticky);
    if (rm) rm.hidden = payload.kind === LINE_KIND.PROMO_GIFT;

    if (payload.kind === LINE_KIND.PROMO_GIFT) {
      const gifts = Array.isArray(getStickyConfig().freeGifts) ? getStickyConfig().freeGifts : [];
      const gMatch = gifts.find((x) => String(x.slot) === String(payload.promoSlot));
      syncPromoGiftBundleTag(slot, gMatch || { slot: payload.promoSlot });
    }
    syncMainSetFreeGiftSlotClass(slot);
    if (scrollIntoView) {
      ensureStickySlotVisibleInItemsRow(slot);
    }
  }

  function lineKeyForFreePouch(btn) {
    return `free-pouch-${btn.getAttribute('data-variant-id')}`;
  }

  function lineKeyForPromoGift(cfgSlot) {
    return `promo-gift-${cfgSlot}`;
  }

  function isFreePouchButton(btn) {
    return btn.hasAttribute('data-is-free-gift');
  }

  function isMainSetPaidButton(btn) {
    return btn != null && btn.hasAttribute('data-is-main-set');
  }

  /** Paid add-on SKUs (not main set, not free pouch). Gated until main set + free pouch are chosen. */
  function isExtrasPaidButton(btn) {
    return btn != null && !isFreePouchButton(btn) && !isMainSetPaidButton(btn);
  }

  function unitCentsFromButton(btn) {
    if (isFreePouchButton(btn)) return 0;
    return parseInt10(btn.getAttribute('data-product-price-cents'), 0);
  }

  function escapeSelectorAttr(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /** True when this slot is the shopper’s main-set choice and the main-set column is a free-gift block. */
  function isStickySlotMainSetFreeGift(slot, bundleRoot) {
    if (!slot || !bundleRoot) return false;
    const vid = slot.dataset.nwVariantId;
    if (!vid) return false;
    const btn = bundleRoot.querySelector(
      `${SEL.ctaButton}[data-is-main-set][data-is-free-gift][data-variant-id="${escapeSelectorAttr(String(vid))}"]`
    );
    if (!btn) return false;
    const kind = slot.dataset.nwLineKind;
    const key = slot.dataset.nwLineKey;
    if (kind === LINE_KIND.FREE_POUCH) {
      return btn.hasAttribute('data-nw-line-key') && btn.getAttribute('data-nw-line-key') === key;
    }
    if (kind === LINE_KIND.PAID) {
      if (!btn.hasAttribute(ATTR_PAID_ACTIVE)) return false;
      const stickyRoot = slot.closest(SEL.stickyBar);
      return stickyRoot ? paidSlotsForVariant(stickyRoot, vid).includes(slot) : false;
    }
    return false;
  }

  function syncMainSetFreeGiftSlotClass(slot) {
    if (!slot) return;
    const root = slot.closest(SEL.bundleRoot);
    slot.classList.toggle(
      CLS_STICKY_MAIN_SET_FREE_GIFT,
      root ? isStickySlotMainSetFreeGift(slot, root) : false
    );
  }

  function paidCtaForVariant(root, variantId) {
    if (!root || variantId == null || String(variantId).trim() === '') return null;
    return root.querySelector(
      `${SEL.ctaButton}[data-variant-id="${escapeSelectorAttr(String(variantId))}"]:not([data-is-free-gift])`
    );
  }

  function slotByLineKey(stickyRoot, lineKey) {
    return stickyRoot.querySelector(`${SEL.stickySlot}[data-nw-line-key="${escapeSelectorAttr(lineKey)}"]`);
  }

  function resetPaidButtonUI(btn) {
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    if (!wrap) return;
    const qtyEl = wrap.querySelector(SEL.qtyWrap);
    btn.classList.remove('hidden');
    btn.removeAttribute('data-nw-line-key');
    btn.removeAttribute(ATTR_PAID_ACTIVE);
    if (qtyEl) {
      qtyEl.classList.add('hidden');
      const input = qtyEl.querySelector(SEL.qtyInput);
      if (input) input.value = '1';
    }
  }

  function deselectPaidLine(btn, stickyRoot, bundleRoot) {
    if (!btn.hasAttribute(ATTR_PAID_ACTIVE)) return;
    const sticky = stickyRoot || getStickyRoot();
    const vid = btn.getAttribute('data-variant-id');
    if (sticky && vid) {
      for (const s of [...paidSlotsForVariant(sticky, vid)]) {
        removePaidSlotElement(s);
      }
    }
    resetPaidButtonUI(btn);
    if (sticky) {
      reconcileAfterStickyChange(sticky, bundleRoot);
    }
  }

  function activatePaidButtonUI(btn) {
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    if (!wrap) return;
    const qtyEl = wrap.querySelector(SEL.qtyWrap);
    btn.classList.add('hidden');
    if (qtyEl) qtyEl.classList.remove('hidden');
  }

  function getFilledSlots(stickyRoot) {
    return [...stickyRoot.querySelectorAll(`${SEL.stickySlot}:not(.nw-bundle-sticky-bar__item--empty)`)];
  }

  /**
   * Total qty across paid lines whose CTA is the main set (data-is-main-set).
   * With theme caps, free pouch uses effectiveFreePouchCap (min of this total and free-pouch setting).
   */
  function mainSetCapacity(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return 0;
    let sum = 0;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind !== LINE_KIND.PAID) continue;
      const vid = slot.dataset.nwVariantId;
      if (!vid) continue;
      const btn = paidCtaForVariant(root, vid);
      if (btn && isMainSetPaidButton(btn)) {
        sum += parseInt10(slot.dataset.nwQty, 1);
      }
    }
    return sum;
  }

  /** Max free pouch slots allowed right now: no main set → 0; else min(main-set qty, theme free-pouch cap). */
  function effectiveFreePouchCap(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return 0;
    const main = mainSetCapacity(stickyRoot, root);
    if (main <= 0) return 0;
    return Math.min(main, getFreePouchTotalQtyCap());
  }

  /**
   * Total qty of paid “extras” lines (add-ons: not main set, not free pouch). Enforces global extras cap.
   */
  function extrasTotalPaidQty(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return 0;
    let sum = 0;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind !== LINE_KIND.PAID) continue;
      const vid = slot.dataset.nwVariantId;
      if (!vid) continue;
      const b = paidCtaForVariant(root, vid);
      if (b && isExtrasPaidButton(b)) {
        sum += parseInt10(slot.dataset.nwQty, 1);
      }
    }
    return sum;
  }

  /** Sum of quantities for sticky lines tagged as free pouch. */
  function freePouchQuantitySum(stickyRoot) {
    if (!stickyRoot) return 0;
    let sum = 0;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind === LINE_KIND.FREE_POUCH) {
        sum += parseInt10(slot.dataset.nwQty, 1);
      }
    }
    return sum;
  }

  /** Extras CTAs unlock only after main set + matching free pouch qty (1:1 when pouch step exists). */
  function extrasFlowUnlocked(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return false;
    return mainSetCapacity(stickyRoot, root) > 0 && isFreePouchRequirementMet(stickyRoot, root);
  }

  function isVisibleBundleColumn(col) {
    if (!col) return false;
    if (col.hidden || col.hasAttribute('hidden')) return false;
    if (col.classList.contains('nw-bundle-item--free-pouch-hidden')) return false;
    return Boolean(col.querySelector(SEL.accordionTrigger));
  }

  /** Bundle PDP/Match Set steps rendered with `nw-bundle-item__accordion-trigger` (see nw-bundle-item.liquid). */
  function bundleAccordionColumns(bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!root) return [];
    return [...root.querySelectorAll(SEL.bundleColumn)].filter(isVisibleBundleColumn);
  }

  function setBundleAccordionOpen(column, open) {
    if (!column) return;
    column.classList.toggle('nw-bundle-item--accordion-open', Boolean(open));
    const trig = column.querySelector(SEL.accordionTrigger);
    if (trig) trig.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /**
   * Phase A: main not chosen — only main step open; header toggles disabled.
   * Phase B: main chosen, free pouch incomplete (0 or fewer than main-set qty) — pouch open / main collapsed;
   *          toggle main+pouch only; extras headers locked until pouch qty matches main-set qty 1:1.
   * Phase C: free pouch requirement met — all section headers can toggle freely; entering from Phase B closes
   *          main + pouch, and opens every accordion column listed after free pouch by default.
   */
  function syncBundleAccordions(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    const sticky = stickyRoot || getStickyRoot();
    if (!root || !sticky) return;
    const cols = bundleAccordionColumns(root);
    if (cols.length === 0) return;

    const mainCol =
      cols.find((el) => el.getAttribute('data-nw-bundle-accordion-role') === 'main') || cols[0];
    const pouchCol = cols.find(
      (el) => el.getAttribute('data-nw-bundle-accordion-role') === 'free-pouch'
    );
    const hasPouchStep = Boolean(pouchCol);

    const mainQty = mainSetCapacity(sticky, root);
    const pouchQty = freePouchQuantitySum(sticky);
    const pouchComplete = !hasPouchStep || pouchQty >= mainQty;

    let phase = 'c';
    if (mainQty === 0) phase = 'a';
    else if (hasPouchStep && !pouchComplete) phase = 'b';

    const prev = root.dataset.nwBundleAccordionPhase || '';
    root.dataset.nwBundleAccordionPhase = phase;

    for (const col of cols) {
      const trig = col.querySelector(SEL.accordionTrigger);
      if (!trig) continue;
      const role = col.getAttribute('data-nw-bundle-accordion-role') || 'extra';
      if (phase === 'a') {
        trig.disabled = true;
      } else if (phase === 'b') {
        trig.disabled = role === 'extra';
      } else {
        trig.disabled = false;
      }
    }

    if (phase === 'a') {
      for (const col of cols) {
        setBundleAccordionOpen(col, col === mainCol);
      }
      return;
    }

    if (phase === 'b' && prev !== 'b') {
      setBundleAccordionOpen(mainCol, false);
      if (pouchCol) setBundleAccordionOpen(pouchCol, true);
      for (const col of cols) {
        if (col === mainCol || col === pouchCol) continue;
        setBundleAccordionOpen(col, false);
      }
      return;
    }

    /* Main set chosen, no free-pouch step — open the next column (extras) and collapse main. */
    if (phase === 'c' && prev === 'a' && mainQty > 0 && !hasPouchStep) {
      setBundleAccordionOpen(mainCol, false);
      const mainIdx = cols.indexOf(mainCol);
      if (mainIdx >= 0 && mainIdx + 1 < cols.length) {
        setBundleAccordionOpen(cols[mainIdx + 1], true);
      }
      for (let j = 0; j < cols.length; j++) {
        if (j === mainIdx || j === mainIdx + 1) continue;
        setBundleAccordionOpen(cols[j], false);
      }
      return;
    }

    /* After free pouch: main + pouch stay closed; open all accordions after the pouch step (DOM order). */
    if (phase === 'c' && prev === 'b' && hasPouchStep) {
      setBundleAccordionOpen(mainCol, false);
      setBundleAccordionOpen(pouchCol, false);
      const pouchIdx = cols.indexOf(pouchCol);
      if (pouchIdx >= 0) {
        for (let j = pouchIdx + 1; j < cols.length; j++) {
          setBundleAccordionOpen(cols[j], true);
        }
      } else {
        for (const col of cols) {
          if (col !== mainCol && col !== pouchCol) setBundleAccordionOpen(col, true);
        }
      }
      return;
    }

    /* Phase C — unlocked; no forced open/close unless handled above (B→C). */
  }

  /** Update one free-pouch sticky cell (single slot can carry qty &gt; 1). */
  function applyFreePouchSlotQuantity(stickyRoot, slot, newQty, bundleRoot) {
    if (!slot || slot.dataset.nwLineKind !== LINE_KIND.FREE_POUCH) return;
    const q = Math.max(1, parseInt10(newQty, 1));
    slot.dataset.nwQty = String(q);
    slot.dataset.nwUnitCents = '0';
    refreshStickyPriceForSlot(slot);
    syncMainSetFreeGiftSlotClass(slot);
    const root = bundleRoot || getBundleRoot();
    const key = slot.dataset.nwLineKey;
    const btn =
      root && key
        ? root.querySelector(
            `${SEL.ctaButton}[data-is-free-gift][data-nw-line-key="${escapeSelectorAttr(String(key))}"]`
          )
        : null;
    if (btn) {
      const inp = btn.closest('.nw-bundle-item__product-item-cta')?.querySelector(SEL.qtyInput);
      if (inp) inp.value = String(q);
    }
  }

  function activateFreePouchButtonUI(btn) {
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    if (!wrap) return;
    const qtyEl = wrap.querySelector(SEL.qtyWrap);
    btn.classList.add('hidden');
    if (qtyEl) {
      qtyEl.classList.remove('hidden');
      const input = qtyEl.querySelector(SEL.qtyInput);
      if (input) input.value = '1';
    }
  }

  function resetFreePouchButtonUI(btn) {
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    if (!wrap) return;
    const qtyEl = wrap.querySelector(SEL.qtyWrap);
    btn.classList.remove('hidden');
    btn.removeAttribute('data-nw-line-key');
    btn.classList.remove(CLS_FREE_POUCH_SELECTED);
    if (qtyEl) {
      qtyEl.classList.add('hidden');
      const input = qtyEl.querySelector(SEL.qtyInput);
      if (input) input.value = '1';
    }
  }

  function deselectFreePouchLine(btn, stickyRoot, bundleRoot) {
    const key = btn.getAttribute('data-nw-line-key');
    const sticky = stickyRoot || getStickyRoot();
    if (!key || !sticky) return;
    const slot = slotByLineKey(sticky, key);
    if (slot) clearStickySlot(slot);
    resetFreePouchButtonUI(btn);
    reconcileAfterStickyChange(sticky, bundleRoot);
  }

  function adjustFreePouchQty(input, delta, stickyRoot, bundleRoot) {
    const wrap = input.closest(SEL.qtyWrap);
    const cta = wrap?.parentElement;
    const btn = cta?.querySelector(SEL.ctaButton);
    if (!btn || !isFreePouchButton(btn) || !btn.hasAttribute('data-nw-line-key')) return;
    const sticky = stickyRoot || getStickyRoot();
    const key = btn.getAttribute('data-nw-line-key');
    const slot = key && sticky ? slotByLineKey(sticky, key) : null;
    if (!slot) return;

    const cap = effectiveFreePouchCap(sticky, bundleRoot);
    const totalUsed = freePouchQuantitySum(sticky);
    const current = parseInt10(slot.dataset.nwQty, 1);
    const othersUsed = totalUsed - current;

    if (delta < 0 && current <= 1) {
      deselectFreePouchLine(btn, sticky, bundleRoot);
      return;
    }

    const maxAttr = parseInt10(input.getAttribute('max'), 100);
    const maxForLine = Math.min(maxAttr, Math.max(0, cap - othersUsed));
    let newQ = current + delta;
    newQ = Math.min(maxForLine, Math.max(1, newQ));

    applyFreePouchSlotQuantity(sticky, slot, newQ, bundleRoot);
    input.value = String(newQ);
    reconcileAfterStickyChange(sticky, bundleRoot);
  }

  /**
   * If main-set qty drops, remove excess free-pouch lines and reset their CTAs (last added first).
   */
  function trimExcessFreePouches(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return;
    let cap = effectiveFreePouchCap(stickyRoot, root);
    let used = freePouchQuantitySum(stickyRoot);
    while (used > cap) {
      const freeSlots = getFilledSlots(stickyRoot).filter((s) => s.dataset.nwLineKind === LINE_KIND.FREE_POUCH);
      if (freeSlots.length === 0) break;
      const slot = freeSlots[freeSlots.length - 1];
      const qs = parseInt10(slot.dataset.nwQty, 1);
      if (qs > 1) {
        applyFreePouchSlotQuantity(stickyRoot, slot, qs - 1, root);
        used = freePouchQuantitySum(stickyRoot);
        continue;
      }
      const key = slot.dataset.nwLineKey;
      clearStickySlot(slot);
      const btn = key ? root.querySelector(`${SEL.ctaButton}[data-nw-line-key="${escapeSelectorAttr(key)}"]`) : null;
      if (btn && isFreePouchButton(btn)) {
        resetFreePouchButtonUI(btn);
      }
      used = freePouchQuantitySum(stickyRoot);
    }
  }

  /** Enable unselected free-gift CTAs only while free-pouch count is below effective cap (theme + main-set qty). */
  function syncFreePouchButtonStates(bundleRoot, stickyRoot) {
    const root = bundleRoot || getBundleRoot();
    const sticky = stickyRoot || getStickyRoot();
    if (!root || !sticky) return;
    const cap = effectiveFreePouchCap(sticky, root);
    const used = freePouchQuantitySum(sticky);
    for (const btn of root.querySelectorAll(`${SEL.ctaButton}[data-is-free-gift]`)) {
      const selected = btn.hasAttribute('data-nw-line-key');
      const lock = !selected && (cap === 0 || used >= cap);
      btn.disabled = lock;
      if (lock) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }

      const wrap = btn.closest('.nw-bundle-item__product-item-cta');
      const inc = wrap?.querySelector(SEL.incQty);
      if (!inc) continue;
      if (!selected) {
        inc.disabled = false;
        continue;
      }
      const lineKey = btn.getAttribute('data-nw-line-key');
      const slot = lineKey ? slotByLineKey(sticky, lineKey) : null;
      const thisQty =
        slot && slot.dataset.nwLineKind === LINE_KIND.FREE_POUCH
          ? parseInt10(slot.dataset.nwQty, 1)
          : 1;
      const othersUsed = used - thisQty;
      const input = wrap.querySelector(SEL.qtyInput);
      const maxAttr = parseInt10(input?.getAttribute('max'), 100);
      const maxAllowed = Math.min(maxAttr, Math.max(0, cap - othersUsed));
      inc.disabled = thisQty >= maxAllowed || maxAllowed <= 0;
    }
  }

  /**
   * If main-set paid qty exceeds the global cap, remove extra main-set slots (last in bar first) and reset CTAs.
   */
  function trimExcessMainSetSelection(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return;
    let used = mainSetCapacity(stickyRoot, root);
    while (used > getMainSetTotalQtyCap()) {
      const mainSlots = getFilledSlots(stickyRoot).filter((s) => {
        if (s.dataset.nwLineKind !== LINE_KIND.PAID) return false;
        const v = s.dataset.nwVariantId;
        if (!v) return false;
        const b = paidCtaForVariant(root, v);
        return b && isMainSetPaidButton(b);
      });
      if (mainSlots.length === 0) break;
      const slot = mainSlots[mainSlots.length - 1];
      const vid = slot.dataset.nwVariantId;
      const paidBtn = vid ? paidCtaForVariant(root, vid) : null;
      removePaidSlotElement(slot);
      if (paidBtn) {
        const remaining = vid ? paidSlotsForVariant(stickyRoot, vid).length : 0;
        if (remaining === 0) {
          resetPaidButtonUI(paidBtn);
        } else {
          const wrap = paidBtn.closest('.nw-bundle-item__product-item-cta');
          const inp = wrap?.querySelector(SEL.qtyInput);
          if (inp) inp.value = String(remaining);
        }
      }
      used = mainSetCapacity(stickyRoot, root);
    }
  }

  /**
   * Remove paid “extras” lines when the step-by-step gate drops (no main set or no free pouch).
   */
  function trimExtrasWhenGated(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return;
    if (extrasFlowUnlocked(stickyRoot, root)) return;
    let progress = true;
    while (progress) {
      progress = false;
      for (const slot of getFilledSlots(stickyRoot)) {
        if (slot.dataset.nwLineKind !== LINE_KIND.PAID) continue;
        const vid = slot.dataset.nwVariantId;
        if (!vid) continue;
        const paidBtn = paidCtaForVariant(root, vid);
        if (!paidBtn || isMainSetPaidButton(paidBtn)) continue;
        removePaidSlotElement(slot);
        const remaining = paidSlotsForVariant(stickyRoot, vid).length;
        if (remaining === 0) {
          resetPaidButtonUI(paidBtn);
        } else {
          const wrap = paidBtn.closest('.nw-bundle-item__product-item-cta');
          const inp = wrap?.querySelector(SEL.qtyInput);
          if (inp) inp.value = String(remaining);
        }
        progress = true;
        break;
      }
    }
  }

  /**
   * If extras paid qty exceeds theme cap, remove extra slots (last in bar first) and reset CTAs.
   */
  function trimExcessExtrasCap(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return;
    const cap = getExtrasTotalQtyCap();
    let used = extrasTotalPaidQty(stickyRoot, root);
    while (used > cap) {
      const extraSlots = getFilledSlots(stickyRoot).filter((s) => {
        if (s.dataset.nwLineKind !== LINE_KIND.PAID) return false;
        const v = s.dataset.nwVariantId;
        if (!v) return false;
        const b = paidCtaForVariant(root, v);
        return b && isExtrasPaidButton(b);
      });
      if (extraSlots.length === 0) break;
      const slot = extraSlots[extraSlots.length - 1];
      const vid = slot.dataset.nwVariantId;
      const paidBtn = vid ? paidCtaForVariant(root, vid) : null;
      removePaidSlotElement(slot);
      if (paidBtn) {
        const remaining = vid ? paidSlotsForVariant(stickyRoot, vid).length : 0;
        if (remaining === 0) {
          resetPaidButtonUI(paidBtn);
        } else {
          const wrap = paidBtn.closest('.nw-bundle-item__product-item-cta');
          const inp = wrap?.querySelector(SEL.qtyInput);
          if (inp) inp.value = String(remaining);
        }
      }
      used = extrasTotalPaidQty(stickyRoot, root);
    }
  }

  /** Disable unselected main-set CTAs when global main-set qty is at cap; disable + on the active row at line max (cap 1). */
  function syncMainSetButtonStates(bundleRoot, stickyRoot) {
    const root = bundleRoot || getBundleRoot();
    const sticky = stickyRoot || getStickyRoot();
    if (!root || !sticky) return;
    const used = mainSetCapacity(sticky, root);
    for (const btn of root.querySelectorAll(`${SEL.ctaButton}[data-is-main-set]:not([data-is-free-gift])`)) {
      const selected = btn.hasAttribute(ATTR_PAID_ACTIVE);
      const lock = !selected && used >= getMainSetTotalQtyCap();
      btn.disabled = lock;
      if (lock) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }

      const wrap = btn.closest('.nw-bundle-item__product-item-cta');
      const inc = wrap?.querySelector(SEL.incQty);
      if (!inc) continue;
      if (!selected) {
        inc.disabled = false;
        continue;
      }
      const vid = btn.getAttribute('data-variant-id');
      const thisN = vid ? paidSlotsForVariant(sticky, vid).length : 0;
      const input = wrap.querySelector(SEL.qtyInput);
      const maxAttr = parseInt10(input?.getAttribute('max'), 100);
      const maxAllowed = Math.min(maxAttr, getMainSetTotalQtyCap() - used + thisN);
      inc.disabled = thisN >= maxAllowed;
    }
  }

  /** Disable extras CTAs until main set and free pouch are both in the bundle; sync + at line max when active. */
  function syncExtrasButtonStates(bundleRoot, stickyRoot) {
    const root = bundleRoot || getBundleRoot();
    const sticky = stickyRoot || getStickyRoot();
    if (!root || !sticky) return;
    const unlock = extrasFlowUnlocked(sticky, root);
    const extrasCap = getExtrasTotalQtyCap();
    const extrasUsed = extrasTotalPaidQty(sticky, root);
    for (const btn of root.querySelectorAll(`${SEL.ctaButton}:not([data-is-free-gift]):not([data-is-main-set])`)) {
      const selected = btn.hasAttribute(ATTR_PAID_ACTIVE);
      const lock = !selected && (!unlock || extrasUsed >= extrasCap);
      btn.disabled = lock;
      if (lock) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }

      const wrap = btn.closest('.nw-bundle-item__product-item-cta');
      const inc = wrap?.querySelector(SEL.incQty);
      if (!inc) continue;
      if (!selected) {
        inc.disabled = false;
        continue;
      }
      if (!unlock) {
        inc.disabled = true;
        continue;
      }
      const vid = btn.getAttribute('data-variant-id');
      const thisN = vid ? paidSlotsForVariant(sticky, vid).length : 0;
      const input = wrap.querySelector(SEL.qtyInput);
      const maxAttr = parseInt10(input?.getAttribute('max'), 100);
      const maxAllowed = Math.min(maxAttr, extrasCap - extrasUsed + thisN);
      inc.disabled = thisN >= maxAllowed;
    }
  }

  /**
   * Paid main set is required for the bundle flow. If the last main-set line is gone but pouch / extras / gifts
   * remain, clear the whole strip and reset CTAs so the shopper starts from a consistent state.
   */
  function clearAllStickySlotsWhenMainSetMissing(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot || !root) return;
    if (mainSetCapacity(stickyRoot, root) > 0) return;
    if (getFilledSlots(stickyRoot).length === 0) return;

    const filled = [...getFilledSlots(stickyRoot)];
    for (const slot of filled) {
      if (!slot.isConnected) continue;
      const kind = slot.dataset.nwLineKind;
      const key = slot.dataset.nwLineKey;

      if (kind === LINE_KIND.PROMO_GIFT) {
        if (slot.hasAttribute('data-nw-dynamic-promo')) {
          slot.remove();
        } else {
          clearStickySlot(slot);
        }
        continue;
      }

      if (kind === LINE_KIND.FREE_POUCH) {
        const fpBtn =
          key != null && key !== ''
            ? root.querySelector(`${SEL.ctaButton}[data-nw-line-key="${escapeSelectorAttr(String(key))}"]`)
            : null;
        if (fpBtn && isFreePouchButton(fpBtn)) {
          resetFreePouchButtonUI(fpBtn);
        }
        if (slot.hasAttribute('data-nw-dynamic-user')) {
          slot.remove();
        } else {
          clearStickySlot(slot);
        }
        continue;
      }

      if (kind === LINE_KIND.PAID) {
        removePaidSlotElement(slot);
        continue;
      }

      clearStickySlot(slot);
    }

    for (const btn of root.querySelectorAll(`${SEL.ctaButton}[${ATTR_PAID_ACTIVE}]`)) {
      resetPaidButtonUI(btn);
    }

    removeTrailingEmptyDynamicUserSlots(stickyRoot);
  }

  /** @type {{ slide: { src: string, srcset: string, alt: string, sizes: string } | null, thumb: { src: string, srcset: string, alt: string, sizes: string } | null, zoomImage: string } | null} */
  let nwBundleGalleryOriginalSnapshot = null;

  function getNwBundleProductGallery() {
    return document.querySelector('.product-gallery.product-gallery--slider');
  }

  function nwBundleShopifyImageWidth(baseUrl, width) {
    if (!baseUrl) return '';
    try {
      const u = new URL(baseUrl, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (_) {
      const sep = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${sep}width=${width}`;
    }
  }

  function snapshotNwBundleGalleryImg(img) {
    if (!img) return null;
    return {
      src: img.getAttribute('src') || '',
      srcset: img.getAttribute('srcset') || '',
      alt: img.getAttribute('alt') || '',
      sizes: img.getAttribute('sizes') || '',
    };
  }

  function applyNwBundleGalleryImgSnapshot(img, snap) {
    if (!img || !snap) return;
    if (snap.src) img.src = snap.src;
    if (snap.srcset) img.setAttribute('srcset', snap.srcset);
    else img.removeAttribute('srcset');
    if (snap.sizes) img.setAttribute('sizes', snap.sizes);
    else img.removeAttribute('sizes');
    if (snap.alt) img.alt = snap.alt;
  }

  function snapshotNwBundleGalleryImages() {
    if (nwBundleGalleryOriginalSnapshot) return nwBundleGalleryOriginalSnapshot;
    const gallery = getNwBundleProductGallery();
    if (!gallery) return null;
    const slideImg = gallery.querySelector('.css-slide-active img');
    const thumbImg = gallery.querySelector('.product-gallery__thumbnails .thumbnail.active img');
    const zoomEl = gallery.querySelector('.css-slide-active product-image-zoom');
    if (!slideImg && !thumbImg) return null;
    nwBundleGalleryOriginalSnapshot = {
      slide: snapshotNwBundleGalleryImg(slideImg),
      thumb: snapshotNwBundleGalleryImg(thumbImg),
      zoomImage: zoomEl?.getAttribute('data-image') || slideImg?.getAttribute('src') || '',
    };
    return nwBundleGalleryOriginalSnapshot;
  }

  function applyNwBundleSetImageToGallery(imageUrl, altText) {
    const gallery = getNwBundleProductGallery();
    if (!gallery || !imageUrl) return;

    const slideImg = gallery.querySelector('.css-slide-active img');
    const thumbImg = gallery.querySelector('.product-gallery__thumbnails .thumbnail.active img');
    const zoomEl = gallery.querySelector('.css-slide-active product-image-zoom');

    if (slideImg) {
      slideImg.src = nwBundleShopifyImageWidth(imageUrl, 1280);
      slideImg.setAttribute(
        'srcset',
        [
          `${nwBundleShopifyImageWidth(imageUrl, 480)} 480w`,
          `${nwBundleShopifyImageWidth(imageUrl, 640)} 640w`,
          `${nwBundleShopifyImageWidth(imageUrl, 960)} 960w`,
          `${nwBundleShopifyImageWidth(imageUrl, 1280)} 1280w`,
        ].join(', '),
      );
      if (altText) slideImg.alt = altText;
    }

    if (thumbImg) {
      thumbImg.src = nwBundleShopifyImageWidth(imageUrl, 120);
      thumbImg.setAttribute(
        'srcset',
        [
          `${nwBundleShopifyImageWidth(imageUrl, 120)} 100w`,
          `${nwBundleShopifyImageWidth(imageUrl, 220)} 200w`,
          `${nwBundleShopifyImageWidth(imageUrl, 320)} 300w`,
        ].join(', '),
      );
      if (altText) thumbImg.alt = altText;
    }

    if (zoomEl) {
      zoomEl.setAttribute('data-image', imageUrl);
    }
  }

  function restoreNwBundleGalleryImages() {
    const snap = snapshotNwBundleGalleryImages();
    const gallery = getNwBundleProductGallery();
    if (!snap || !gallery) return;
    applyNwBundleGalleryImgSnapshot(gallery.querySelector('.css-slide-active img'), snap.slide);
    applyNwBundleGalleryImgSnapshot(
      gallery.querySelector('.product-gallery__thumbnails .thumbnail.active img'),
      snap.thumb,
    );
    const zoomEl = gallery.querySelector('.css-slide-active product-image-zoom');
    if (zoomEl && snap.zoomImage) {
      zoomEl.setAttribute('data-image', snap.zoomImage);
    }
  }

  /** Main-set selection → swap PDP gallery active slide + active thumbnail to set image (`data-product-image-original`). */
  function syncNwBundleMainSetGallery(bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    const gallery = getNwBundleProductGallery();
    if (!root || !gallery) return;

    snapshotNwBundleGalleryImages();

    const mainBtn = root.querySelector(
      `${SEL.ctaButton}[data-is-main-set]:not([data-is-free-gift])[${ATTR_PAID_ACTIVE}]`,
    );
    if (!mainBtn) {
      restoreNwBundleGalleryImages();
      return;
    }

    const imageUrl =
      mainBtn.getAttribute('data-product-image-original') ||
      mainBtn.getAttribute('data-product-image') ||
      '';
    if (!imageUrl) return;

    applyNwBundleSetImageToGallery(imageUrl, mainBtn.getAttribute('data-product-title') || '');
  }

  function reconcileAfterStickyChange(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    syncFreePouchCapVisibility(root, stickyRoot);
    clearAllStickySlotsWhenMainSetMissing(stickyRoot, root);
    trimExcessMainSetSelection(stickyRoot, root);
    trimExcessFreePouches(stickyRoot, root);
    trimExtrasWhenGated(stickyRoot, root);
    trimExcessExtrasCap(stickyRoot, root);
    if (movableSlotsHaveLeadingGap(stickyRoot)) {
      recompactMovableStickySlots(stickyRoot);
    }
    syncPromoFreeGifts(stickyRoot);
    syncBundleAccordions(stickyRoot, root);
    updateSummary(stickyRoot, root);
    syncNwBundleMainSetGallery(root);
  }

  /**
   * Subtotal in cents for shipping & tier eligibility: paid lines only (exclude free pouch + promo gift).
   * Free-gift thresholds use {@link giftQualificationSubtotalCents} (tier-discounted paid subtotal).
   */
  function subtotalAmountCents(stickyRoot) {
    let sum = 0;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind !== LINE_KIND.PAID) continue;
      const unit = parseInt10(slot.dataset.nwUnitCents, 0);
      const qty = parseInt10(slot.dataset.nwQty, 1);
      sum += unit * qty;
    }
    return sum;
  }

  /**
   * Subtotal for free-gift thresholds: paid lines only, then active tier % off.
   * Free pouch is always £0 in the builder (100% discount applied at cart) — never counts toward thresholds or tiers.
   */
  function giftQualificationSubtotalCents(stickyRoot) {
    const cfg = getStickyConfig();
    const paidSubtotal = subtotalAmountCents(stickyRoot);
    return tierDiscountedAmountCents(paidSubtotal, cfg.tieredDiscounts || [], paidSubtotal);
  }

  /** @param {number} subtotalCents Paid bundle subtotal (minor units), before discount codes. */
  function tierMinAmountCents(tier) {
    if (tier == null) return 0;
    if (tier.minAmount != null && tier.minAmount !== '') return Number(tier.minAmount);
    if (tier.minItems != null && tier.minItems !== '') return Number(tier.minItems);
    return 0;
  }

  function pickActiveTieredDiscount(tieredDiscounts, subtotalCents) {
    if (!Array.isArray(tieredDiscounts) || tieredDiscounts.length === 0) return null;
    const sorted = [...tieredDiscounts].sort((a, b) => tierMinAmountCents(b) - tierMinAmountCents(a));
    for (const tier of sorted) {
      if (subtotalCents >= tierMinAmountCents(tier)) return tier;
    }
    return null;
  }

  /** Apply active tier %; tier eligibility uses pre-discount {@link tierBasisCents} (defaults to amountCents). */
  function tierDiscountedAmountCents(amountCents, tieredDiscounts, tierBasisCents) {
    const basis = tierBasisCents != null ? tierBasisCents : amountCents;
    const tier = pickActiveTieredDiscount(tieredDiscounts, basis);
    const pct = tier != null ? Number(tier.valuePercent) : NaN;
    if (tier && Number.isFinite(pct) && pct > 0) {
      return Math.round(amountCents * (1 - pct / 100));
    }
    return amountCents;
  }

  /** Product grid: tier badge, discounted price, and strikethrough pre-tier total (× line qty when selected). */
  function getBundleGridCardQty(btn, stickyRoot) {
    if (!btn?.hasAttribute(ATTR_PAID_ACTIVE)) return 1;
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    const input = wrap?.querySelector(SEL.qtyInput);
    if (input) return Math.max(1, parseInt10(input.value, 1));
    const vid = btn.getAttribute('data-variant-id');
    if (!stickyRoot || !vid) return 1;
    return Math.max(1, paidSlotsForVariant(stickyRoot, vid).length);
  }

  function restoreBundleGridCardPrice(card, btn) {
    const unitCents = parseInt10(btn.getAttribute('data-product-price-cents'), 0);
    const priceCurrent =
      card.querySelector('[data-nw-mix-grid-price-current]') ||
      card.querySelector('.nw-bundle-item__product-item-price-current');
    const tierStrike = card.querySelector('[data-nw-mix-grid-tier-strike]');
    const compareAt = card.querySelector('[data-nw-mix-grid-compare-at]');
    if (priceCurrent && unitCents > 0) priceCurrent.textContent = formatMoney(unitCents);
    if (tierStrike) {
      tierStrike.textContent = '';
      tierStrike.hidden = true;
      tierStrike.classList.add('hidden');
      tierStrike.setAttribute('aria-hidden', 'true');
    }
    if (compareAt) {
      const compareCents = parseInt10(compareAt.getAttribute('data-nw-compare-at-cents'), 0);
      const showCompare = compareCents > unitCents;
      compareAt.hidden = !showCompare;
      compareAt.classList.toggle('hidden', !showCompare);
      compareAt.toggleAttribute('aria-hidden', !showCompare);
      if (showCompare) compareAt.textContent = formatMoney(compareCents);
    }
  }

  /** Paid product grids in [data-nw-bundle-main]: tier badge, price, and strike when sticky subtotal unlocks a tier. */
  function syncGridTierDiscountLabels(bundleRoot, stickyRoot, activeTier) {
    const root = bundleRoot || getBundleRoot();
    const sticky = stickyRoot || getStickyRoot();
    if (!root) return;
    const pct = activeTier != null ? Number(activeTier.valuePercent) : NaN;
    const show = Boolean(activeTier && Number.isFinite(pct) && pct > 0);
    for (const card of root.querySelectorAll('.nw-bundle-item__product-item')) {
      const btn = card.querySelector(`${SEL.ctaButton}:not([data-is-free-gift])`);
      if (!btn) continue;
      const badge = card.querySelector('[data-nw-mix-grid-tier-discount]');
      const priceCurrent =
        card.querySelector('[data-nw-mix-grid-price-current]') ||
        card.querySelector('.nw-bundle-item__product-item-price-current');
      const tierStrike = card.querySelector('[data-nw-mix-grid-tier-strike]');
      const compareAt = card.querySelector('[data-nw-mix-grid-compare-at]');
      const unitCents = parseInt10(btn.getAttribute('data-product-price-cents'), 0);

      if (badge) {
        if (show) {
          badge.textContent = `-${Math.round(pct)}%`;
          badge.removeAttribute('hidden');
          badge.classList.remove('hidden');
        } else {
          badge.textContent = '';
          badge.setAttribute('hidden', '');
          badge.classList.add('hidden');
        }
      }

      if (!priceCurrent || unitCents <= 0) continue;

      if (show) {
        const qty = getBundleGridCardQty(btn, sticky);
        const originalTotal = unitCents * qty;
        const discountedTotal = Math.round(originalTotal * (1 - pct / 100));
        priceCurrent.textContent = formatMoney(discountedTotal);
        if (tierStrike) {
          tierStrike.textContent = formatMoney(originalTotal);
          tierStrike.hidden = false;
          tierStrike.classList.remove('hidden');
          tierStrike.removeAttribute('aria-hidden');
        }
        if (compareAt) {
          compareAt.hidden = true;
          compareAt.classList.add('hidden');
          compareAt.setAttribute('aria-hidden', 'true');
        }
      } else {
        restoreBundleGridCardPrice(card, btn);
      }
    }
  }

  function hasUserSelectedBundleItems(stickyRoot) {
    return getFilledSlots(stickyRoot).some(
      (s) =>
        s.dataset.nwLineKind === LINE_KIND.PAID || s.dataset.nwLineKind === LINE_KIND.FREE_POUCH
    );
  }

  function updateStickyBarVisibility(stickyRoot) {
    if (!stickyRoot) return;
    stickyRoot.classList.toggle('visible', hasUserSelectedBundleItems(stickyRoot));
  }

  function syncNwBundleSelectedLines(stickyRoot) {
    if (!stickyRoot) {
      window.nwBundleSelectedLines = [];
      return;
    }
    const mainCtx = getMainSetParentContext(getBundleRoot());
    const lines = [];
    for (const slot of getFilledSlots(stickyRoot)) {
      const vid = slot.dataset.nwVariantId || null;
      const isMainSet =
        mainCtx.variantId != null && vid != null && String(vid) === String(mainCtx.variantId);
      const parentId = !isMainSet && mainCtx.variantId != null ? String(mainCtx.variantId) : null;
      lines.push({
        lineKey: slot.dataset.nwLineKey || null,
        kind: slot.dataset.nwLineKind || null,
        variantId: vid,
        productId: slot.dataset.nwProductId || null,
        quantity: parseInt10(slot.dataset.nwQty, 1),
        sellingPlanId: slot.dataset.nwSellingPlanId || null,
        parentId,
      });
    }
    window.nwBundleSelectedLines = lines;
  }

  /** One summary row per threshold promo gift (DOM strip order); placeholder row when none. */
  function renderSummaryPromoGifts(stickyRoot) {
    const mount = stickyRoot?.querySelector('[data-nw-summary-gifts-mount]');
    if (!mount) return;
    mount.replaceChildren();
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    const ordered = items
      ? [...items.querySelectorAll(SEL.stickySlot)].filter(
          (s) =>
            s.dataset.nwLineKind === LINE_KIND.PROMO_GIFT &&
            !s.classList.contains('nw-bundle-sticky-bar__item--empty')
        )
      : [];

    function appendRow(titleText, valueText) {
      const row = document.createElement('div');
      row.className = 'nw-bundle-sticky-bar__summary-item';
      const titleEl = document.createElement('div');
      titleEl.className = 'nw-bundle-sticky-bar__summary-item-title';
      titleEl.textContent = titleText;
      const valEl = document.createElement('div');
      valEl.className = 'nw-bundle-sticky-bar__summary-item-value';
      valEl.textContent = valueText;
      row.append(titleEl, valEl);
      mount.appendChild(row);
    }

    if (ordered.length === 0) {
      appendRow('Gift', '—');
      return;
    }

    ordered.forEach((slot, i) => {
      const t = slot.querySelector(SEL.stickyTitle);
      const name = (t && t.textContent.trim()) || 'Free gift';
      const label = ordered.length > 1 ? `Gift ${i + 1}` : 'Gift';
      appendRow(label, name);
    });
  }

  /** Empty free-shipping milestone slot: switch placeholder → active art when paid subtotal meets shipping threshold. */
  function syncShippingMilestoneSlotVisuals(stickyRoot, subtotalCents) {
    if (!stickyRoot) return;
    const cfg = getStickyConfig();
    const shipTh = parseInt10(cfg.shippingThreshold, 0);
    const qualified = shipTh > 0 && subtotalCents >= shipTh;
    for (const slot of stickyRoot.querySelectorAll('[data-nw-bundle-shipping-milestone]')) {
      if (!slot.classList.contains('nw-bundle-sticky-bar__item--empty')) {
        slot.classList.remove('nw-bundle-sticky-bar__item--shipping-unlocked');
        continue;
      }
      const placeholder = slot.querySelector('.nw-bundle-sticky-bar__item-placeholder');
      const prodImg = slot.querySelector(SEL.stickyImg);
      const actSrc = (prodImg && prodImg.getAttribute('data-nw-bundle-tag-active-src')) || '';
      const phImg = slot.querySelector('.nw-bundle-sticky-bar__item-placeholder-img');
      const phSrc =
        (phImg && phImg.getAttribute('data-nw-shipping-placeholder-src')) || (phImg && phImg.src) || '';
      let fallback = placeholder?.querySelector('[data-nw-shipping-milestone-fallback-img]') || null;
      if (qualified && actSrc) {
        if (phImg) {
          phImg.src = actSrc;
          phImg.hidden = false;
        } else if (placeholder) {
          if (!fallback) {
            fallback = document.createElement('img');
            fallback.className = 'nw-bundle-sticky-bar__item-placeholder-img';
            fallback.setAttribute('data-nw-shipping-milestone-fallback-img', '');
            fallback.alt = '';
            fallback.width = 40;
            fallback.height = 40;
            placeholder.appendChild(fallback);
          }
          fallback.src = actSrc;
          fallback.hidden = false;
          placeholder.querySelector('svg')?.setAttribute('hidden', '');
        }
        slot.classList.add('nw-bundle-sticky-bar__item--shipping-unlocked');
      } else {
        if (phImg && phSrc) phImg.src = phSrc;
        if (phImg) phImg.hidden = false;
        if (fallback) {
          fallback.remove();
        }
        placeholder?.querySelector('svg')?.removeAttribute('hidden');
        slot.classList.remove('nw-bundle-sticky-bar__item--shipping-unlocked');
      }
    }
  }

  /** Compact summary (v2): savings line, "{pct}% Discount", strikethrough pre-tier total + discounted total. */
  function updateSummaryV2(stickyRoot, subtotal, cfg) {
    const wrap = stickyRoot?.querySelector('[data-nw-summary-v2-wrap]');
    if (!wrap) return;
    const tierBlock = wrap.querySelector('[data-nw-summary-v2-tier-block]');
    const saveEl = wrap.querySelector('[data-nw-summary-v2-save]');
    const discEl = wrap.querySelector('[data-nw-summary-v2-discount]');
    const origEl = wrap.querySelector('[data-nw-summary-v2-price-original]');
    const finalEl = wrap.querySelector('[data-nw-summary-v2-price-final]');
    const savePrefix = (wrap.dataset.savePrefix || 'You save').trim();
    const discountSuffix = (wrap.dataset.discountSuffix || 'Discount').trim();

    const tier = pickActiveTieredDiscount(cfg.tieredDiscounts || [], subtotal);
    const pct = tier != null ? Number(tier.valuePercent) : NaN;
    const showTierPromo = Boolean(tier && Number.isFinite(pct) && pct > 0);

    if (tierBlock) tierBlock.hidden = !showTierPromo;

    if (showTierPromo) {
      const discounted = Math.round(subtotal * (1 - pct / 100));
      const saveCents = Math.max(0, subtotal - discounted);
      if (saveEl) saveEl.textContent = `${savePrefix} ${formatMoney(saveCents)}`.trim();
      if (discEl) discEl.textContent = `${pct}% ${discountSuffix}`.trim();
      if (origEl) {
        origEl.textContent = formatMoney(subtotal);
        origEl.hidden = false;
      }
      if (finalEl) finalEl.textContent = formatMoney(bundleCheckoutOneTimeDisplayCents(stickyRoot, subtotal));
    } else {
      if (origEl) {
        origEl.textContent = '';
        origEl.hidden = true;
      }
      if (finalEl) finalEl.textContent = formatMoney(bundleCheckoutOneTimeDisplayCents(stickyRoot, subtotal));
    }
  }

  function updateSummary(stickyRoot, bundleRoot) {
    const cfg = getStickyConfig();
    const itemsLabel = stickyRoot?.querySelector('[data-nw-summary-items-label]');
    const subEl = stickyRoot?.querySelector('[data-nw-summary-subtotal]');
    const shipEl = stickyRoot?.querySelector('[data-nw-summary-shipping]');
    const tierRow = stickyRoot?.querySelector('[data-nw-summary-tier-row]');
    const tierEl = stickyRoot?.querySelector('[data-nw-summary-tier]');

    const subtotal = subtotalAmountCents(stickyRoot);
    updateSummaryV2(stickyRoot, subtotal, cfg);
    const totalPieces = getFilledSlots(stickyRoot).reduce((acc, s) => acc + parseInt10(s.dataset.nwQty, 1), 0);

    if (itemsLabel) {
      const prefix = totalPieces === 1 ? '1 item ' : `${totalPieces} items `;
      const prefixEl = itemsLabel.querySelector('.nw-bundle-sticky-bar__summary-items-label-prefix');
      const selectedEl = itemsLabel.querySelector('.nw-bundle-sticky-bar__summary-items-label-selected');
      if (prefixEl && selectedEl) {
        prefixEl.textContent = prefix;
        selectedEl.textContent = 'selected';
      } else {
        itemsLabel.textContent =
          totalPieces === 1 ? '1 item selected' : `${totalPieces} items selected`;
      }
    }
    if (subEl) {
      const tierForDisplay = pickActiveTieredDiscount(cfg.tieredDiscounts, subtotal);
      const pctD = tierForDisplay != null ? Number(tierForDisplay.valuePercent) : NaN;
      if (tierForDisplay && Number.isFinite(pctD) && pctD > 0) {
        const discounted = Math.round(subtotal * (1 - pctD / 100));
        subEl.innerHTML = `<span class="nw-bundle-sticky-bar__subtotal-original">${formatMoney(subtotal)}</span> <span class="nw-bundle-sticky-bar__subtotal-discounted">${formatMoney(discounted)}</span>`;
      } else {
        subEl.textContent = formatMoney(subtotal);
      }
    }

    const shipTh = parseInt10(cfg.shippingThreshold, 0);
    if (shipEl) {
      if (shipTh <= 0) {
        shipEl.textContent = '—';
      } else if (subtotal >= shipTh) {
        shipEl.textContent = 'Free';
      } else {
        const remain = shipTh - subtotal;
        shipEl.textContent = `${formatMoney(remain)} to free shipping`;
      }
    }

    syncShippingMilestoneSlotVisuals(stickyRoot, subtotal);

    const tier = pickActiveTieredDiscount(cfg.tieredDiscounts, subtotal);
    if (tierRow && tierEl) {
      const pct = tier ? Number(tier.valuePercent) : NaN;
      if (tier && Number.isFinite(pct) && pct > 0) {
        tierRow.hidden = false;
        tierEl.textContent = `${pct}% off`;
      } else {
        tierRow.hidden = true;
        tierEl.textContent = '—';
      }
    }

    syncGridTierDiscountLabels(bundleRoot || getBundleRoot(), stickyRoot, tier);

    renderSummaryPromoGifts(stickyRoot);

    updateStickyBarVisibility(stickyRoot);
    syncNwBundleSelectedLines(stickyRoot);
    syncFreePouchButtonStates(bundleRoot || getBundleRoot(), stickyRoot);
    syncMainSetButtonStates(bundleRoot || getBundleRoot(), stickyRoot);
    syncExtrasButtonStates(bundleRoot || getBundleRoot(), stickyRoot);
    syncAddToCartButtonsState(stickyRoot, bundleRoot || getBundleRoot());
    updatePurchaseBlockPrices(stickyRoot, bundleRoot || getBundleRoot());
  }

  function slotHasSellingPlan(slot) {
    const sp = slot.dataset.nwSellingPlanId;
    return sp != null && String(sp).trim() !== '';
  }

  /** True if any selected paid line can subscribe (sticky carries selling plan id). */
  function hasAnySubscriptionEligiblePaidLine(stickyRoot) {
    if (!stickyRoot) return false;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind === LINE_KIND.PAID && slotHasSellingPlan(slot)) return true;
    }
    return false;
  }

  /**
   * Subscription column total: full one-time for non-subscription SKUs, +10%-off only on lines with a selling plan.
   */
  function bundleBlendedSubscriptionSubtotalCents(stickyRoot) {
    if (!stickyRoot) return 0;
    let sum = 0;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind !== LINE_KIND.PAID) continue;
      const unit = parseInt10(slot.dataset.nwUnitCents, 0);
      const qty = parseInt10(slot.dataset.nwQty, 1);
      const lineCents = unit * qty;
      if (slotHasSellingPlan(slot)) {
        sum += Math.round(lineCents * (1 - NW_PURCHASE_SUBSCRIPTION_DISCOUNT));
      } else {
        sum += lineCents;
      }
    }
    return sum;
  }

  /** Sticky v2 final price and purchase one-time option: tier-discounted paid subtotal. */
  function bundleCheckoutOneTimeDisplayCents(stickyRoot, subtotalCents) {
    const subtotal = subtotalCents != null ? subtotalCents : subtotalAmountCents(stickyRoot);
    const cfg = getStickyConfig();
    return tierDiscountedAmountCents(subtotal, cfg.tieredDiscounts || [], subtotal);
  }

  /** Purchase subscription option: blended subscription subtotal with tier discount applied. */
  function bundleCheckoutSubscriptionDisplayCents(stickyRoot) {
    const subtotal = subtotalAmountCents(stickyRoot);
    const blended = bundleBlendedSubscriptionSubtotalCents(stickyRoot);
    const cfg = getStickyConfig();
    return tierDiscountedAmountCents(blended, cfg.tieredDiscounts || [], subtotal);
  }

  /** Hide subscription CTAs when no paid sticky line has a selling plan (same rule as the subscribe purchase radio). */
  function syncSubscriptionPromoVisibility(stickyRoot, bundleRoot) {
    if (!stickyRoot) return;
    const hasSubEligible = hasAnySubscriptionEligiblePaidLine(stickyRoot);

    const stickySubscribeItem = stickyRoot.querySelector('[data-nw-sticky-subscribe-cta]');
    if (stickySubscribeItem) {
      stickySubscribeItem.classList.toggle('hidden', !hasSubEligible);
      stickySubscribeItem.toggleAttribute('hidden', !hasSubEligible);
    }

    const root = bundleRoot || getBundleRoot();
    const purchaseSubscribeCta = root?.querySelector('[data-nw-purchase-subscribe-cta]');
    if (purchaseSubscribeCta) {
      purchaseSubscribeCta.classList.toggle('hidden', !hasSubEligible);
      purchaseSubscribeCta.toggleAttribute('hidden', !hasSubEligible);
    }
  }

  /** One-time = paid subtotal; subscription = blended total. Visibility follows block setting; sub-eligibility only affects prices and default radio. */
  function updatePurchaseBlockPrices(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot) return;

    syncSubscriptionPromoVisibility(stickyRoot, root);

    if (!root) return;
    const purchase = root.querySelector('.nw-bundle-purchase');
    if (!purchase) return;
    const purchaseSubscriptionEnabled = purchase.dataset.nwPurchaseSubscriptionEnabled !== 'false';
    const onetimeEl = purchase.querySelector('[data-nw-purchase-onetime-price]');
    const subscriptionEl = purchase.querySelector('[data-nw-purchase-subscription-price]');
    if (!purchaseSubscriptionEnabled || !onetimeEl || !subscriptionEl) return;

    const optWrap = purchase.querySelector('.nw-bundle-purchase__purchase-option');
    const subDesc = purchase.querySelector('[data-nw-purchase-subscription-description]');
    const subRow = purchase.querySelector('[data-nw-purchase-subscription-row]');

    const onetimeCents = subtotalAmountCents(stickyRoot);
    const hasSubEligible = hasAnySubscriptionEligiblePaidLine(stickyRoot);

    if (optWrap) optWrap.hidden = false;
    if (subDesc) subDesc.hidden = false;
    if (subRow) subRow.hidden = !hasSubEligible;

    if (!hasSubEligible) {
      const subRadio = purchase.querySelector('input[name="nw-bundle-purchase"][value="subscription"]');
      const oneRadio = purchase.querySelector('input[name="nw-bundle-purchase"][value="onetime"]');
      if (oneRadio) oneRadio.checked = true;
      if (subRadio) subRadio.checked = false;
    }

    if (onetimeCents <= 0) {
      onetimeEl.textContent = '—';
      subscriptionEl.textContent = '—';
      return;
    }
    onetimeEl.textContent = formatMoney(bundleCheckoutOneTimeDisplayCents(stickyRoot, onetimeCents));
    if (hasSubEligible) {
      subscriptionEl.textContent = formatMoney(bundleCheckoutSubscriptionDisplayCents(stickyRoot));
    } else {
      subscriptionEl.textContent = '—';
    }
  }

  function promoGiftQualifies(g, subtotal) {
    if (!g || g.enabled === false) return false;
    const vid = g.variantId;
    if (vid == null || vid === '' || Number(vid) === 0) return false;
    const th = Number.parseInt(String(g.threshold), 10);
    return Number.isFinite(th) && th > 0 && subtotal >= th;
  }

  /** Pill under promo-gift cells (dynamic template clones have no Liquid-rendered tags). */
  function syncPromoGiftBundleTag(slot, giftCfg) {
    const cfg = getStickyConfig();
    const t = cfg.bundleFreeGiftTag;
    if (!t || !t.enabled || !slot) return;
    if (!slot.hasAttribute('data-nw-dynamic-promo')) return;

    let wrap = slot.querySelector('.nw-bundle-sticky-bar__item-discount-tags');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'nw-bundle-sticky-bar__item-discount-tags';
      slot.appendChild(wrap);
    }

    let span = wrap.querySelector('[data-nw-promo-gift-tag]');
    if (!span) {
      span = document.createElement('span');
      span.setAttribute('data-nw-promo-gift-tag', '');
      span.className =
        'nw-bundle-sticky-bar__item-discount-tag nw-bundle-sticky-bar__item-bundle-tag nw-bundle-sticky-bar__item-bundle-tag--free-gift';
      wrap.appendChild(span);
    }

    const base = (t.label && String(t.label).trim()) || 'Free gift';
    const n = giftCfg != null && giftCfg.slot != null ? Number(giftCfg.slot) : 0;
    span.textContent = n > 1 ? `${base} ${n}` : base;
    span.style.setProperty('--discount-tag-bg', t.bg || '#FFC107');
    span.style.setProperty('--discount-tag-color', t.fg || '#201D1E');
  }

  function refreshAllPromoGiftBundleTags(stickyRoot) {
    if (!stickyRoot) return;
    const gifts = Array.isArray(getStickyConfig().freeGifts) ? getStickyConfig().freeGifts : [];
    for (const slot of stickyRoot.querySelectorAll(
      `${SEL.stickySlot}[data-nw-line-kind="${LINE_KIND.PROMO_GIFT}"]`
    )) {
      const ps = slot.dataset.nwPromoSlot;
      const g = gifts.find((x) => String(x.slot) === String(ps));
      syncPromoGiftBundleTag(slot, g || { slot: ps });
    }
  }

  /** Drop threshold promos that no longer qualify; keep existing cells so their order stays fixed. */
  function removeUnqualifiedPromoGifts(stickyRoot, gifts, giftSubtotalCents) {
    if (!stickyRoot) return;
    for (const slot of [
      ...stickyRoot.querySelectorAll(`${SEL.stickySlot}[data-nw-line-kind="${LINE_KIND.PROMO_GIFT}"]`),
    ]) {
      const lineKey = slot.dataset.nwLineKey;
      if (!lineKey) continue;
      const g = gifts.find((x) => lineKeyForPromoGift(x.slot) === lineKey);
      if (!promoGiftQualifies(g, giftSubtotalCents)) {
        if (slot.hasAttribute('data-nw-dynamic-promo')) {
          slot.remove();
        } else {
          clearStickySlot(slot);
        }
      }
    }
  }

  function syncPromoFreeGifts(stickyRoot) {
    const cfg = getStickyConfig();
    const gifts = Array.isArray(cfg.freeGifts) ? [...cfg.freeGifts] : [];
    const giftSubtotal = giftQualificationSubtotalCents(stickyRoot);
    removeUnqualifiedPromoGifts(stickyRoot, gifts, giftSubtotal);

    gifts.sort((a, b) => {
      const ta = Number.parseInt(String(a.threshold), 10);
      const tb = Number.parseInt(String(b.threshold), 10);
      return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
    });

    for (const g of gifts) {
      if (!promoGiftQualifies(g, giftSubtotal)) continue;
      const lineKey = lineKeyForPromoGift(g.slot);
      const staticSlot = findStaticPromoGiftMilestoneSlot(stickyRoot, g.slot);
      let existing = slotByLineKey(stickyRoot, lineKey);

      if (
        existing &&
        staticSlot &&
        existing !== staticSlot &&
        existing.hasAttribute('data-nw-dynamic-promo')
      ) {
        existing.remove();
        existing = null;
      }

      if (existing) continue;

      let slot = staticSlot;
      if (!slot) {
        const insertAfter = findLastFilledItemSlot(stickyRoot);
        slot = insertDynamicPromoSlotAfter(stickyRoot, insertAfter);
      }
      if (!slot) continue;
      fillStickySlot(slot, {
        lineKey,
        kind: LINE_KIND.PROMO_GIFT,
        variantId: g.variantId,
        productId: g.productId != null ? String(g.productId) : null,
        sellingPlanId: null,
        title: g.productName || 'Gift',
        image: g.featuredImage || '',
        unitCents: 0,
        qty: 1,
        priceLabel: 'Free',
        promoSlot: String(g.slot),
      });
    }

    refreshAllPromoGiftBundleTags(stickyRoot);
  }

  /** Scroll so the step heading sits below sticky header / sticky bar (after accordion has opened). */
  function scrollBundleColumnIntoView(column) {
    if (!column) return;
    const target = column.querySelector(SEL.accordionTrigger) || column;
    const rect = target.getBoundingClientRect();
    const scrollY = window.scrollY ?? window.pageYOffset ?? 0;
    const top = rect.top + scrollY - NW_BUNDLE_STEP_SCROLL_OFFSET_PX;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function isAccordionPanelExpanded(column) {
    const inner = column?.querySelector('.nw-bundle-item__accordion-collapse-inner');
    if (!inner) return false;
    return inner.getBoundingClientRect().height > 8;
  }

  /**
   * Run `cb` after the accordion expand transition (or soon after if already open / no transition).
   * @param {HTMLElement} column
   * @param {() => void} cb
   */
  function whenAccordionExpandDone(column, cb) {
    if (!column || typeof cb !== 'function') return;
    const run = () => requestAnimationFrame(() => requestAnimationFrame(cb));
    if (!column.classList.contains('nw-bundle-item--accordion-open')) {
      run();
      return;
    }
    if (isAccordionPanelExpanded(column)) {
      run();
      return;
    }
    const collapse = column.querySelector('.nw-bundle-item__accordion-collapse');
    if (!collapse) {
      run();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      collapse.removeEventListener('transitionend', onEnd);
      run();
    };
    const onEnd = (e) => {
      if (e.target !== collapse || e.propertyName !== 'grid-template-rows') return;
      finish();
    };
    collapse.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, 400);
  }

  /**
   * Open the next step, wait for it to expand, then scroll — so the opened section is visible.
   * Used only after gift-set or free-pouch selection — not for paid extras.
   * @param {HTMLElement} triggerBtn — CTA inside the current column
   * @param {HTMLElement} [bundleRoot]
   */
  function advanceBundleStepFromButton(triggerBtn, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!root || !triggerBtn || !root.contains(triggerBtn)) return;
    const current = triggerBtn.closest(SEL.bundleColumn);
    if (!current || !isVisibleBundleColumn(current)) return;
    const columns = bundleAccordionColumns(root);
    const i = columns.indexOf(current);
    if (i < 0 || i >= columns.length - 1) return;
    const nextCol = columns[i + 1];
    setBundleAccordionOpen(current, false);
    setBundleAccordionOpen(nextCol, true);
    whenAccordionExpandDone(nextCol, () => scrollBundleColumnIntoView(nextCol));
  }

  function refreshStickyPriceForSlot(slot) {
    const kind = slot.dataset.nwLineKind;
    const unit = parseInt10(slot.dataset.nwUnitCents, 0);
    const qty = parseInt10(slot.dataset.nwQty, 1);
    const priceEl = slot.querySelector(SEL.stickyPrice);
    if (!priceEl) return;
    if (kind === LINE_KIND.FREE_POUCH || kind === LINE_KIND.PROMO_GIFT) {
      priceEl.textContent = 'Free';
      return;
    }
    priceEl.textContent = formatMoney(unit * qty);
  }

  function onCtaClick(btn, stickyRoot, bundleRoot) {
    const sticky = stickyRoot || getStickyRoot();
    if (!sticky) return;

    if (isFreePouchButton(btn)) {
      const key = lineKeyForFreePouch(btn);
      const existing = slotByLineKey(sticky, key);
      if (existing) {
        deselectFreePouchLine(btn, sticky, bundleRoot);
        return;
      }
      const cap = effectiveFreePouchCap(sticky, bundleRoot);
      const used = freePouchQuantitySum(sticky);
      if (cap === 0 || used >= cap) return;
      const slot = findFirstEmptySlot(sticky);
      if (!slot) return;
      fillStickySlot(slot, {
        lineKey: key,
        kind: LINE_KIND.FREE_POUCH,
        variantId: btn.getAttribute('data-variant-id'),
        productId: btn.getAttribute('data-product-id'),
        sellingPlanId: btn.getAttribute('data-selling-plan-id'),
        title: btn.getAttribute('data-product-title') || '',
        image: btn.getAttribute('data-product-image') || '',
        unitCents: 0,
        qty: 1,
        priceLabel: 'Free',
      });
      btn.setAttribute('data-nw-line-key', key);
      btn.classList.add(CLS_FREE_POUCH_SELECTED);
      activateFreePouchButtonUI(btn);
      reconcileAfterStickyChange(sticky, bundleRoot);
      if (isFreePouchRequirementMet(sticky, bundleRoot)) {
        requestAnimationFrame(() => advanceBundleStepFromButton(btn, bundleRoot));
      }
      return;
    }

    if (btn.hasAttribute(ATTR_PAID_ACTIVE)) return;

    if (!addPaidUnitSlot(sticky, btn, bundleRoot)) return;
    btn.setAttribute(ATTR_PAID_ACTIVE, 'true');
    activatePaidButtonUI(btn);
    reconcileAfterStickyChange(sticky, bundleRoot);
    if (isMainSetPaidButton(btn)) {
      requestAnimationFrame(() => advanceBundleStepFromButton(btn, bundleRoot));
    }
  }

  function onRemoveSticky(slot, bundleRoot) {
    const sticky = slot.closest(SEL.stickyBar);
    if (!sticky) return;
    const key = slot.dataset.nwLineKey;
    const kind = slot.dataset.nwLineKind;
    const root = bundleRoot || getBundleRoot();

    if (kind === LINE_KIND.PROMO_GIFT) {
      if (slot.hasAttribute('data-nw-dynamic-promo')) {
        slot.remove();
      } else {
        clearStickySlot(slot);
      }
      reconcileAfterStickyChange(sticky, bundleRoot);
      return;
    }

    if (kind === LINE_KIND.FREE_POUCH) {
      const fpBtn = root?.querySelector(`${SEL.ctaButton}[data-nw-line-key="${escapeSelectorAttr(key)}"]`) || null;
      if (fpBtn && isFreePouchButton(fpBtn)) {
        resetFreePouchButtonUI(fpBtn);
      }
      clearStickySlot(slot);
      reconcileAfterStickyChange(sticky, bundleRoot);
      return;
    }

    if (kind === LINE_KIND.PAID) {
      const vid = slot.dataset.nwVariantId;
      const paidBtn =
        (vid &&
          root?.querySelector(
            `${SEL.ctaButton}[data-variant-id="${escapeSelectorAttr(vid)}"]:not([data-is-free-gift])`
          )) ||
        null;
      removePaidSlotElement(slot);
      if (paidBtn) {
        const remaining = vid ? paidSlotsForVariant(sticky, vid).length : 0;
        if (remaining === 0) {
          resetPaidButtonUI(paidBtn);
        } else {
          const wrap = paidBtn.closest('.nw-bundle-item__product-item-cta');
          const input = wrap?.querySelector(SEL.qtyInput);
          if (input) input.value = String(remaining);
        }
      }
      reconcileAfterStickyChange(sticky, bundleRoot);
      return;
    }

    clearStickySlot(slot);
    reconcileAfterStickyChange(sticky, bundleRoot);
  }

  function adjustQty(input, delta, stickyRoot, bundleRoot) {
    const wrap = input.closest(SEL.qtyWrap);
    const cta = wrap?.parentElement;
    const btn = cta?.querySelector(SEL.ctaButton);
    if (btn && isFreePouchButton(btn) && btn.hasAttribute('data-nw-line-key')) {
      adjustFreePouchQty(input, delta, stickyRoot, bundleRoot);
      return;
    }
    if (!btn || !btn.hasAttribute(ATTR_PAID_ACTIVE)) return;
    const sticky = stickyRoot || getStickyRoot();
    const vid = btn.getAttribute('data-variant-id');
    if (!sticky || !vid) return;
    const root = bundleRoot || getBundleRoot();
    if (isExtrasPaidButton(btn) && root && !extrasFlowUnlocked(sticky, root) && delta > 0) return;

    const current = parseInt10(input.value, 1);
    if (delta < 0 && current <= 1) {
      deselectPaidLine(btn, sticky, bundleRoot);
      return;
    }

    let q = current + delta;
    const maxAttr = parseInt10(input.getAttribute('max'), 100);
    let maxAllowed = maxAttr;
    if (isMainSetPaidButton(btn) && root) {
      const used = mainSetCapacity(sticky, root);
      const thisN = paidSlotsForVariant(sticky, vid).length;
      maxAllowed = Math.min(maxAttr, getMainSetTotalQtyCap() - used + thisN);
    } else if (isExtrasPaidButton(btn) && root) {
      const used = extrasTotalPaidQty(sticky, root);
      const thisN = paidSlotsForVariant(sticky, vid).length;
      maxAllowed = Math.min(maxAttr, getExtrasTotalQtyCap() - used + thisN);
    }
    const min = parseInt10(input.getAttribute('min'), 1);
    q = Math.min(maxAllowed, Math.max(min, q));
    syncPaidSlotsToQty(sticky, bundleRoot, btn, q);
    input.value = String(paidSlotsForVariant(sticky, vid).length);
    reconcileAfterStickyChange(sticky, bundleRoot);
  }

  function bind(bundleRoot) {
    const stickyRoot = getStickyRoot();
    if (!stickyRoot) return;

    bundleRoot.addEventListener('click', (e) => {
      const accTrig = e.target.closest(SEL.accordionTrigger);
      if (
        accTrig &&
        bundleRoot.contains(accTrig) &&
        !accTrig.disabled &&
        accTrig.closest(SEL.bundleRoot) === bundleRoot
      ) {
        e.preventDefault();
        const col = accTrig.closest(SEL.bundleColumn);
        if (col) {
          const open = col.classList.toggle('nw-bundle-item--accordion-open');
          accTrig.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        return;
      }
      const purchaseSubscribeCta = e.target.closest('.nw-bundle-purchase__cta-button-subscribe');
      if (purchaseSubscribeCta && bundleRoot.contains(purchaseSubscribeCta)) {
        e.preventDefault();
        if (purchaseSubscribeCta.disabled) return;
        nwBundleAddToCart('subscription', bundleRoot, purchaseSubscribeCta);
        return;
      }
      const purchaseCta = e.target.closest('.nw-bundle-purchase__cta-button');
      if (purchaseCta && bundleRoot.contains(purchaseCta)) {
        e.preventDefault();
        if (purchaseCta.disabled) return;
        nwBundleAddToCart(nwBundlePurchaseModeFromBundleRoot(bundleRoot), bundleRoot, purchaseCta);
        return;
      }
      const btn = e.target.closest(SEL.ctaButton);
      if (btn && bundleRoot.contains(btn)) {
        e.preventDefault();
        onCtaClick(btn, stickyRoot, bundleRoot);
        return;
      }
      const dec = e.target.closest(SEL.decQty);
      if (dec && bundleRoot.contains(dec)) {
        const input = dec.closest(SEL.qtyWrap)?.querySelector(SEL.qtyInput);
        if (input) adjustQty(input, -1, stickyRoot, bundleRoot);
        return;
      }
      const inc = e.target.closest(SEL.incQty);
      if (inc && bundleRoot.contains(inc)) {
        if (inc.disabled) return;
        const input = inc.closest(SEL.qtyWrap)?.querySelector(SEL.qtyInput);
        if (input) adjustQty(input, 1, stickyRoot, bundleRoot);
        return;
      }
});

    stickyRoot.addEventListener('click', (e) => {
      const stickyBuyNow = e.target.closest('.nw-bundle-sticky-bar__cta-item-button-buy-now');
      if (stickyBuyNow) {
        e.preventDefault();
        if (stickyBuyNow.disabled) return;
        nwBundleAddToCart(nwBundlePurchaseModeFromBundleRoot(bundleRoot), bundleRoot, stickyBuyNow);
        return;
      }
      const stickySubscribe = e.target.closest('.nw-bundle-sticky-bar__cta-item-button-subscribe');
      if (stickySubscribe) {
        e.preventDefault();
        if (stickySubscribe.disabled) return;
        nwBundleAddToCart('subscription', bundleRoot, stickySubscribe);
        return;
      }
      const stickyOnetime = e.target.closest('.nw-bundle-sticky-bar__cta-item-button');
      if (stickyOnetime) {
        e.preventDefault();
        if (stickyOnetime.disabled) return;
        nwBundleAddToCart('onetime', bundleRoot, stickyOnetime);
        return;
      }
      const rm = e.target.closest(SEL.removeSticky);
      if (!rm) return;
      const slot = rm.closest(SEL.stickySlot);
      if (slot) onRemoveSticky(slot, bundleRoot);
    });

    bundleRoot.addEventListener('change', (e) => {
      const input = e.target.closest(SEL.qtyInput);
      if (!input || !bundleRoot.contains(input)) return;
      const btn = input.closest('.nw-bundle-item__product-item-cta')?.querySelector(SEL.ctaButton);
      if (!btn) return;

      if (isFreePouchButton(btn) && btn.hasAttribute('data-nw-line-key')) {
        const sticky = stickyRoot || getStickyRoot();
        const key = btn.getAttribute('data-nw-line-key');
        const slot = key && sticky ? slotByLineKey(sticky, key) : null;
        if (!slot) return;
        const cap = effectiveFreePouchCap(sticky, bundleRoot);
        const totalUsed = freePouchQuantitySum(sticky);
        const current = parseInt10(slot.dataset.nwQty, 1);
        const othersUsed = totalUsed - current;
        let max = Math.min(parseInt10(input.getAttribute('max'), 100), Math.max(0, cap - othersUsed));
        const min = parseInt10(input.getAttribute('min'), 1);
        let q = parseInt10(input.value, 1);
        if (q <= 0) {
          deselectFreePouchLine(btn, sticky, bundleRoot);
          return;
        }
        q = Math.min(max, Math.max(min, q));
        applyFreePouchSlotQuantity(sticky, slot, q, bundleRoot);
        input.value = String(q);
        reconcileAfterStickyChange(sticky, bundleRoot);
        return;
      }

      if (!btn.hasAttribute(ATTR_PAID_ACTIVE)) return;
      const vid = btn.getAttribute('data-variant-id');
      if (!vid) return;
      let q = parseInt10(input.value, 1);
      let max = parseInt10(input.getAttribute('max'), 100);
      const rootCh = bundleRoot || getBundleRoot();
      if (isMainSetPaidButton(btn)) {
        if (rootCh) {
          const used = mainSetCapacity(stickyRoot, rootCh);
          const thisN = paidSlotsForVariant(stickyRoot, vid).length;
          max = Math.min(max, getMainSetTotalQtyCap() - used + thisN);
        }
      } else if (isExtrasPaidButton(btn) && rootCh) {
        if (!extrasFlowUnlocked(stickyRoot, rootCh)) {
          max = Math.min(max, paidSlotsForVariant(stickyRoot, vid).length);
        } else {
          const used = extrasTotalPaidQty(stickyRoot, rootCh);
          const thisN = paidSlotsForVariant(stickyRoot, vid).length;
          max = Math.min(max, getExtrasTotalQtyCap() - used + thisN);
        }
      }
      const min = parseInt10(input.getAttribute('min'), 1);
      if (q <= 0) {
        deselectPaidLine(btn, stickyRoot, bundleRoot);
        return;
      }
      q = Math.min(max, Math.max(min, q));
      syncPaidSlotsToQty(stickyRoot, bundleRoot, btn, q);
      input.value = String(paidSlotsForVariant(stickyRoot, vid).length);
      reconcileAfterStickyChange(stickyRoot, bundleRoot);
    });

    reconcileAfterStickyChange(stickyRoot, bundleRoot);
  }

  function init() {
    document.querySelectorAll(SEL.bundleRoot).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
